import React, { useState, useEffect } from "react"
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  useTheme,
  Collapse,
  Grid,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  alpha,
  Card,
  styled,
  Checkbox,
  TableSortLabel,
  TextField,
  Alert,
  Menu,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  FormControlLabel,
  Chip,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material"
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Payment,
  Person,
  LocationOn,
  LocationCity,
  Home,
  PinDrop,
  Public,
  ShoppingCart,
  Update as UpdateIcon,
  CheckCircle,
  Schedule,
  LocalShippingOutlined,
  Cancel,
  Refresh,
  FileDownload,
  SelectAll,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  LocalShipping as DeliveryIcon,
  Assignment as ChallanIcon,
  Email as EmailIcon,
  Send as SendIcon,
  Add as AddIcon
} from "@mui/icons-material"
import apiService from "Network/apiService"
import { API_ENDPOINTS } from "Network/apiEndpoints"
import buttonConfig from "config/ConfigAccessButton"
import {
  submitNewPaidPaymentStatus,
  buildNewPaidPaymentFormData,
  extractPaymentApiError,
  getOrderPaymentDetailsFromOrder,
  validateNewPaidPaymentRequest,
  validatePaidPaymentFormDetails,
  isZeroRemainingPayment,
  PAYMENT_UPDATE_LOCKED_ORDER_STATUSES,
  getOrderPaymentStatusValue,
  isPaymentUpdateDisabledForOrder,
  getPaymentUpdateDisabledReason,
  DEFAULT_PAYMENT_UPDATE_MODAL,
  getPaidPaymentStepState,
  getAdvancePaymentEntries,
} from "Network/paymentStatusService"
import {
  fetchAdvancePaymentMethods,
  buildPaymentMethodsById,
} from "Network/advancePaymentMethodService"
import { useToast } from "OtherReusableFunctionality/ToastContext"
import {
  PaymentDetailsStepContent,
  PAYMENT_DETAILS_DIALOG_CONTENT_SX,
  PAYMENT_DETAILS_DIALOG_ACTIONS_SX,
  PAYMENT_DETAILS_DIALOG_PAPER_PROPS,
} from "../Products/productList"

const StatusChip = styled(Card)(({ status, theme }) => {
  const getStatusConfig = (status) => {
    const statusMap = {
      pending: {
        bg: theme.palette.warning.light,
        text: theme.palette.warning.dark,
        icon: <Schedule />
      },
      confirmed: {
        bg: theme.palette.info.light,
        text: theme.palette.info.dark,
        icon: <CheckCircle />
      },
      processing: {
        bg: theme.palette.primary.light,
        text: theme.palette.primary.dark,
        icon: <Schedule />
      },
      shipped: {
        bg: theme.palette.secondary.light,
        text: theme.palette.secondary.dark,
        icon: <LocalShippingOutlined />
      },
      delivered: {
        bg: theme.palette.success.light,
        text: theme.palette.success.dark,
        icon: <CheckCircle />
      },
      cancelled: {
        bg: theme.palette.error.light,
        text: theme.palette.error.dark,
        icon: <Cancel />
      },
      returned: {
        bg: theme.palette.error.dark,
        text: theme.palette.common.white,
        icon: <Refresh />
      }
    }
    return statusMap[status?.toLowerCase()] || {
      bg: theme.palette.grey[300],
      text: theme.palette.grey[800],
      icon: null
    }
  }

  const config = getStatusConfig(status)
  return {
    backgroundColor: config.bg,
    color: config.text,
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 12px',
    borderRadius: 16,
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'capitalize',
    '& svg': {
      fontSize: '1rem',
      marginRight: 4
    }
  }
})

const PaymentStatusChip = styled(Chip)(({ status, theme }) => {
  const getPaymentStatusConfig = (status) => {
    const statusMap = {
      pending: {
        bg: '#fcc874', // Orange/amber - matching image
        text: '#FFFFFF',
        icon: <Schedule sx={{ fontSize: '16px !important' }} />
      },
      outstanding: {
        bg: '#888cff', // Moderate purple - matching image
        text: '#FFFFFF',
        icon: <Schedule sx={{ fontSize: '16px !important' }} />
      },
      paid: {
        bg: '#22d28a', // Vibrant green - matching image
        text: '#FFFFFF',
        icon: <CheckCircle sx={{ fontSize: '16px !important' }} />
      },
      failed: {
        bg: '#f4346e', // Vibrant red - matching image
        text: '#FFFFFF',
        icon: <Cancel sx={{ fontSize: '16px !important' }} />
      },
      refunded: {
        bg: '#888cff', // Lighter purple/lavender - matching image
        text: '#FFFFFF',
        icon: <CheckCircle sx={{ fontSize: '16px !important' }} />
      }
    }
    return statusMap[status?.toLowerCase()] || {
      bg: theme.palette.grey[400],
      text: '#FFFFFF',
      icon: null
    }
  }

  const config = getPaymentStatusConfig(status)
  return {
    backgroundColor: config.bg,
    color: config.text,
    fontWeight: 600,
    textTransform: 'capitalize',
    borderRadius: theme.shape.borderRadius,
    height: 'auto',
    padding: '4px 10px',
    minWidth: 'auto',
    '& .MuiChip-icon': {
      color: config.text,
      marginLeft: '4px',
      marginRight: '4px'
    },
    '& .MuiChip-label': {
      padding: '0 4px',
      fontSize: '0.875rem',
      color: config.text,
      fontWeight: 600
    }
  }
})

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: "16px 20px",
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  whiteSpace: "nowrap",
  "&.header": {
      fontWeight: 600,
      fontSize: "0.875rem",
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
      color: theme.palette.text.primary,
      position: 'sticky',
      top: 0,
      zIndex: 1,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
      transform: 'translateY(-1px)',
      boxShadow: `0 4px 8px ${alpha(theme.palette.common.black, 0.1)}`,
  },
  "& > .MuiTableCell-root": {
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  },
}));

const ExpandableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  '& > .MuiTableCell-root': {
    borderBottom: 'none',
  }
}));

const DetailCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  '&:last-child': {
    marginBottom: 0
  },
  '& h6': {
    marginBottom: theme.spacing(2)
  },
  '& .MuiDivider-root': {
    margin: theme.spacing(2, 0)
  }
}))

const InvoiceModal = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '90vw',
    maxHeight: '90vh',
    width: '1200px',
    height: '800px',
    margin: theme.spacing(2),
  },
}));

const InvoiceFrame = styled('iframe')(({ theme }) => ({
  width: '100%',
  height: '100%',
  border: 'none',
  borderRadius: theme.shape.borderRadius,
}));

const VEHICLE_REFERENCE_NO_REGEX = /^[A-Z]{2}\s\d{2}\s[A-Z]{2}\s\d{4}$/;
const VEHICLE_REFERENCE_NO_ERROR = 'Enter valid format: MH 12 AB 1234';
const formatVehicleReferenceNo = (value = '') => {
  const cleanedValue = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
  const part1 = cleanedValue.slice(0, 2).replace(/[^A-Z]/g, '');
  const part2 = cleanedValue.slice(2, 4).replace(/[^0-9]/g, '');
  const part3 = cleanedValue.slice(4, 6).replace(/[^A-Z]/g, '');
  const part4 = cleanedValue.slice(6, 10).replace(/[^0-9]/g, '');
  return [part1, part2, part3, part4].filter(Boolean).join(' ');
};
const isVehicleReferenceNoInvalid = (value = '') => {
  const normalizedValue = String(value || '').trim();
  return normalizedValue !== '' && !VEHICLE_REFERENCE_NO_REGEX.test(normalizedValue);
};

const BulkProductList = ({ 
  data: bulkProducts = [], 
  searchQuery = '', 
  statusFilter = 'all', 
  isLoading = false, 
  onRefresh,
  selectedProducts = [],
  onProductSelect,
  onSelectAll,
  isAllSelected = false,
  onExport,
  onZipDownload,
  currentPage = 1,
  pageSize = 10,
  shouldEnableButton, 
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set())
  const [statusUpdateModal, setStatusUpdateModal] = useState({ 
    open: false, 
    order: null,
    cancellationReason: '',
    returnReason: '',
     entityName: '',
    orderBy: '',
    driverName: '',
    vehicleNo: '',
    shippedDate: new Date().toISOString().slice(0,10),
    generateInvoice: false,
    isCreatingChallan: false,
    challanError: '',
    emails: [],
    emailInput: '',
    dispatchPersonName: '',
    customDispatchPerson: '',
    useCustomDispatchPerson: false,
    start_km: '',   // new
    end_km: ''
  })
  const [paymentUpdateModal, setPaymentUpdateModal] = useState(() => ({ ...DEFAULT_PAYMENT_UPDATE_MODAL }))
  const [invoiceModal, setInvoiceModal] = useState({ open: false, invoice: null, product: null })
  const [deliveryChallanModal, setDeliveryChallanModal] = useState({ open: false, challan: null, product: null })
  const [bulkChallanModal, setBulkChallanModal] = useState({
    open: false,
    orderBy: '',
    entityName: '',
    driverName: '',
    vehicleNo: '',
    deliveryDate: new Date().toISOString().slice(0,10),
    generateInvoice: false,
    isCreating: false,
    error: '',
    emails: [],
    emailInput: '',
    selectedStatus: '',
    selectedStatusCategory: '',
    dispatchPersonName: '',
    customDispatchPerson: '',
    useCustomDispatchPerson: false,
     start_km: '',   // new
    end_km: '',
    cancellationReason: '',
    returnReason: '',
    bulkPaymentStatus: ''  // For bulk payment status update
  })
  const DEFAULT_BULK_PAYMENT_STATUS_MODAL = {
    open: false,
    selectedPaymentStatus: '',
    orderPaymentDetails: null,
    error: '',
    isUpdating: false,
    step: 'status',
    paymentOption: '',
    advanceAmount: '',
    paymentMethod: '',
    paymentMethodConfig: null,
    paymentFields: {},
    paymentFiles: {},
    paymentError: '',
    summaryOrder: null,
    installmentAmount: '',
    creditNote: '',
  }

  const [bulkPaymentStatusModal, setBulkPaymentStatusModal] = useState({
    ...DEFAULT_BULK_PAYMENT_STATUS_MODAL,
  })
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [updatingPayment, setUpdatingPayment] = useState(false)
  const [isUpdatingBulkPayment, setIsUpdatingBulkPayment] = useState(false)
  const [downloadingInvoice, setDownloadingInvoice] = useState(false)
  const [downloadingChallan, setDownloadingChallan] = useState(false)
  const theme = useTheme()
  const toast = useToast()

  const [statusOptions, setStatusOptions] = useState([])
  const [paymentStatusChoices, setPaymentStatusChoices] = useState([])
  const [loadingPaymentStatusChoices, setLoadingPaymentStatusChoices] = useState(false)
  const [advancePaymentMethods, setAdvancePaymentMethods] = useState([])
  const [loadingAdvancePaymentMethods, setLoadingAdvancePaymentMethods] = useState(false)
  const paymentMethodsById = React.useMemo(
    () => buildPaymentMethodsById(advancePaymentMethods),
    [advancePaymentMethods]
  )
  const [dispatchPersons, setDispatchPersons] = useState([])
  const [orderByChoices, setOrderByChoices] = useState([])
  const [bulkEntityOptions, setBulkEntityOptions] = useState([])
  const [loadingBulkEntities, setLoadingBulkEntities] = useState(false)
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null)
  const isActionsMenuOpen = Boolean(actionsAnchorEl)
  const openActionsMenu = (e) => setActionsAnchorEl(e.currentTarget)
  const closeActionsMenu = () => setActionsAnchorEl(null)
  const [emailDialog, setEmailDialog] = useState({
    open: false,
    emails: '',
    sendInvoice: true,
    sendDelivery: true,
    isSending: false,
    error: '',
    success: ''
  })
  const [singleEmailDialog, setSingleEmailDialog] = useState({
    open: false,
    order: null,
    emailInput: '',
    emails: [],
    sendInvoice: true,
    sendDelivery: true,
    isSending: false,
    error: '',
    success: ''
  })

  const getCategoryIcon = (category) => {
    const key = (typeof category === 'string' ? category : (category?.name ?? '')).toLowerCase()
    switch (key) {
      case 'pending': return <Schedule />
      case 'confirmed': return <CheckCircle />
      case 'processing': return <Schedule />
      case 'shipped': return <LocalShippingOutlined />
      case 'delivered': return <CheckCircle />
      case 'cancelled': return <Cancel />
      case 'returned': return <Refresh />
      default: return null
    }
  }

  // Helper functions for sequential status validation
  // Build status order dynamically from API response using sequence field
  const getStatusOrder = () => {
    if (!statusOptions || statusOptions.length === 0) {
      return {};
    }
    
    // Build order map using category as key and sequence as value
    const orderMap = {};
    statusOptions.forEach(option => {
      const category = (option.category || '').toLowerCase();
      const sequence = option.sequence || 0;
      if (category) {
        orderMap[category] = sequence;
      }
    });
    
    return orderMap;
  };

  // Get the next sequential status based on current status
  const getNextSequentialStatus = (currentStatusName, currentStatusCategory) => {
    if (!statusOptions || statusOptions.length === 0) {
      return null;
    }

    const statusOrder = getStatusOrder();
    let currentStatusLower = (currentStatusCategory || '').toLowerCase();
    let currentOrder = statusOrder[currentStatusLower] || 0;

    // If category not found, try to find it from statusOptions by name
    if (currentOrder === 0 && currentStatusName) {
      const currentStatusOption = statusOptions.find(opt => opt.name === currentStatusName);
      if (currentStatusOption) {
        currentStatusLower = (currentStatusOption.category || '').toLowerCase();
        currentOrder = statusOrder[currentStatusLower] || 0;
      }
    }

    // Delivered orders can only move to Returned.
    if (currentStatusLower === 'delivered' || (currentStatusName || '').toLowerCase() === 'delivered') {
      return statusOptions.find(option =>
        (option.category || '').toLowerCase() === 'returned' ||
        (option.name || '').toLowerCase() === 'returned'
      ) || null;
    }

    // If current order is 0 or not found, return null (can't determine next)
    if (currentOrder === 0) {
      return null;
    }

    // Find the next sequential status (currentOrder + 1)
    const nextOrder = currentOrder + 1;
    const nextStatus = statusOptions.find(option => {
      const optionCategory = (option.category || '').toLowerCase();
      return statusOrder[optionCategory] === nextOrder;
    });

    return nextStatus || null;
  };

  const getStatusesToDisable = (currentStatusCategory, currentStatusName) => {
    if (!statusOptions || statusOptions.length === 0) {
      return [];
    }
    
    const statusOrder = getStatusOrder();
    let currentStatusLower = (currentStatusCategory || '').toLowerCase();
    let currentOrder = statusOrder[currentStatusLower] || 0;
    
    // If category not found, try to find it from statusOptions by name
    if (currentOrder === 0 && currentStatusName) {
      const currentStatusOption = statusOptions.find(opt => opt.name === currentStatusName);
      if (currentStatusOption) {
        currentStatusLower = (currentStatusOption.category || '').toLowerCase();
        currentOrder = statusOrder[currentStatusLower] || 0;
      }
    }
    
    // Find Dispatch, Processing, and Delivered status orders
    const dispatchOrder = statusOrder['shipped'] || statusOrder['dispatch'] || 999;
    const processingOrder = statusOrder['processing'] || 0;
    const deliveredOrder = statusOrder['delivered'] || 999;
    
    // Check if current status is Processing (by name or category)
    const isProcessingStatus = currentStatusName?.toLowerCase() === 'processing' || 
                                currentStatusLower === 'processing' ||
                                currentOrder === processingOrder;
    
    // Check if current status is Delivered
    const isDeliveredStatus = currentStatusName?.toLowerCase() === 'delivered' || 
                              currentStatusLower === 'delivered' ||
                              currentOrder === deliveredOrder;
    
    // Check if current status is Cancelled
    const isCancelledStatus = currentStatusName?.toLowerCase() === 'cancelled' || 
                              currentStatusLower === 'cancelled';
    
    // If still not found, disable all
    if (currentOrder === 0) {
      return statusOptions
        .filter(option => {
          const optionCategory = (option.category || '').toLowerCase();
          const optionName = (option.name || '').toLowerCase();
          // Always disable Dispatch if not at Processing
          if (optionCategory === 'shipped' || optionCategory === 'dispatch' || optionName === 'dispatch') {
            return true; // Disable Dispatch
          }
          return true; // Disable all others
        })
        .map(option => option.category?.toLowerCase() || '');
    }
    
    // Special case: If status is Cancelled, no further status changes allowed
    if (isCancelledStatus) {
      return statusOptions.map(option => option.category?.toLowerCase() || '');
    }
    
    // Special case: If status is Delivered, only allow Returned
    if (isDeliveredStatus) {
      return statusOptions.filter(option => {
        const optionCategory = (option.category || '').toLowerCase();
        const optionName = (option.name || '').toLowerCase();
        const isReturned = optionCategory === 'returned' || optionName === 'returned';
        // Only allow Returned, disable everything else
        return !isReturned;
      }).map(option => option.category?.toLowerCase() || '');
    }
    
    // Disable statuses that:
    // 1. Come before the current status (can't go backwards)
    // 2. Come more than 1 step after current status (must go sequentially)
    // 3. Dispatch is only available when current status is Processing
    // 4. Returned is only available from Delivered status
    return statusOptions.filter(option => {
      const optionCategory = (option.category || '').toLowerCase();
      const optionName = (option.name || '').toLowerCase();
      const isReturned = optionCategory === 'returned' || optionName === 'returned';
      const isCancelled = optionCategory === 'cancelled' || optionName === 'cancelled';
      const isDispatch = optionCategory === 'shipped' || optionCategory === 'dispatch' || optionName === 'dispatch';
      
      // Dispatch is only available when current status is Processing
      if (isDispatch) {
        return !isProcessingStatus; // Disable Dispatch if not at Processing
      }
      
      // Returned is only available from Delivered status
      if (isReturned) {
        return !isDeliveredStatus; // Disable Returned if not at Delivered
      }
      
      const statusOrderValue = statusOrder[optionCategory] || 0;
      const dispatchOrder = statusOrder['shipped'] || statusOrder['dispatch'] || 999;
      
      // Disable if it's before current status (can't go backwards)
      // This includes previous statuses like Placed, Confirmed when current is Processing
      if (statusOrderValue < currentOrder) {
        return true;
      }
      
      // Disable current status itself (must move forward, not stay the same)
      if (statusOrderValue === currentOrder) {
        return true;
      }
      
      // Special case: Cancelled can be selected from any status up to and including Dispatch
      // (Placed, Confirmed, Processing, Dispatch - all can be cancelled)
      if (isCancelled) {
        // Allow Cancelled if current status is up to Dispatch (including Dispatch)
        if (currentOrder <= dispatchOrder) {
          return false; // Allow Cancelled
        } else {
          return true; // Disable Cancelled if already past Dispatch (e.g., Delivered)
        }
      }
      
      // Disable if it's more than 1 step ahead (enforce sequential progression)
      if (statusOrderValue > currentOrder + 1) {
        return true;
      }
      
      // Only allow next sequential status (currentOrder + 1)
      return false;
    }).map(option => option.category?.toLowerCase() || '');
  };

  useEffect(() => {
    console.log("statusUpdateModal.order", statusUpdateModal)
  }, [statusUpdateModal])
  const getAvailableCounts = () => {
    const selected = filteredData.filter(p => selectedProducts.includes(p.id))
    const invoices = selected.filter(p => p.invoice && p.invoice.path).length
    const challans = selected.filter(p => p.delivery_challan && p.delivery_challan.path).length
    return { invoices, challans }
  }

  // Check if any selected product has status that should disable documents/email
  const shouldDisableDocumentsAndEmail = () => {
    const selected = filteredData.filter(p => selectedProducts.includes(p.id))
    const restrictedStatuses = ['Placed', 'Confirmed', 'Processing']
    return selected.some(p => {
      const statusName = p.status?.name || p.status_name || ''
      return restrictedStatuses.includes(statusName)
    })
  }

  const handleOpenEmailDialog = () => {
    setEmailDialog(prev => ({ ...prev, open: true, error: '', success: '' }))
    closeActionsMenu()
  }

  const handleCloseEmailDialog = () => {
    setEmailDialog({ open: false, emails: '', sendInvoice: true, sendDelivery: true, isSending: false, error: '', success: '' })
  }

  const handleEmailDialogChange = (field, value) => {
    setEmailDialog(prev => ({ ...prev, [field]: value, error: '', success: '' }))
  }

  const handleEmailSendSubmit = async () => {
    const { emails, sendInvoice, sendDelivery } = emailDialog
    const userId = localStorage.getItem('user_id')
    const selectedOrderIds = filteredData
      .filter(p => selectedProducts.includes(p.id))
      .map(p => p.order_id)
      .filter(Boolean)

    // Basic validations
    const trimmed = (emails || '').split(/[,\s]+/).map(e => e.trim()).filter(Boolean)
    if (trimmed.length === 0) {
      setEmailDialog(prev => ({ ...prev, error: 'Please enter at least one email address' }))
      return
    }
    if (!sendInvoice && !sendDelivery) {
      setEmailDialog(prev => ({ ...prev, error: 'Please select at least one document type to send' }))
      return
    }
    const { invoices, challans } = getAvailableCounts()
    if ((sendInvoice && invoices === 0) && (sendDelivery && challans === 0)) {
      setEmailDialog(prev => ({ ...prev, error: 'No available documents to send for selected orders' }))
      return
    }

    setEmailDialog(prev => ({ ...prev, isSending: true, error: '', success: '' }))
    try {
      const payload = {
        emails: trimmed.join(','),
        order_id: selectedOrderIds.join(','),
        user_id: userId,
        invoice: !!sendInvoice,
        delivery: !!sendDelivery
      }

      const res = await apiService.post(
        API_ENDPOINTS.POST_send_challan_on_email,
        JSON.stringify(payload),
        'application/json'
      )

      if (res?.success) {
        setEmailDialog(prev => ({ ...prev, success: 'Email sent successfully', isSending: false }))
        setTimeout(() => handleCloseEmailDialog(), 1200)
      } else {
        throw new Error(res?.message || 'Failed to send email')
      }
    } catch (err) {
      setEmailDialog(prev => ({ ...prev, error: err?.message || 'Failed to send email', isSending: false }))
    }
  }

  // Individual email dialog handlers
  const openSingleOrderEmail = (product) => {
    const hasInvoice = !!(product?.invoice && product.invoice.path)
    const hasChallan = !!(product?.delivery_challan && product.delivery_challan.path)
    setSingleEmailDialog({
      open: true,
      order: product,
      emailInput: '',
      emails: [],
      sendInvoice: hasInvoice,
      sendDelivery: hasChallan,
      isSending: false,
      error: '',
      success: ''
    })
  }

  const closeSingleOrderEmail = () => {
    setSingleEmailDialog({
      open: false,
      order: null,
      emailInput: '',
      emails: [],
      sendInvoice: true,
      sendDelivery: true,
      isSending: false,
      error: '',
      success: ''
    })
  }

  const addEmailToSingle = () => {
    const input = (singleEmailDialog.emailInput || '').trim()
    if (!input) return
    // basic email check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(input)) {
      setSingleEmailDialog(prev => ({ ...prev, error: 'Enter a valid email address' }))
      return
    }
    if (singleEmailDialog.emails.includes(input)) {
      setSingleEmailDialog(prev => ({ ...prev, error: 'Email already added' }))
      return
    }
    setSingleEmailDialog(prev => ({ ...prev, emails: [...prev.emails, input], emailInput: '', error: '' }))
  }

  const removeEmailFromSingle = (email) => {
    setSingleEmailDialog(prev => ({ ...prev, emails: prev.emails.filter(e => e !== email) }))
  }

  const submitSingleOrderEmail = async () => {
    const { order, emails, sendInvoice, sendDelivery } = singleEmailDialog
    if (!order) return
    if (emails.length === 0) {
      setSingleEmailDialog(prev => ({ ...prev, error: 'Please add at least one email' }))
      return
    }
    if (!sendInvoice && !sendDelivery) {
      setSingleEmailDialog(prev => ({ ...prev, error: 'Please select at least one document type' }))
      return
    }
    // Check availability for requested types
    const hasInvoice = !!(order?.invoice && order.invoice.path)
    const hasChallan = !!(order?.delivery_challan && order.delivery_challan.path)
    if ((sendInvoice && !hasInvoice) && (sendDelivery && !hasChallan)) {
      setSingleEmailDialog(prev => ({ ...prev, error: 'Selected documents are not available for this order' }))
      return
    }

    setSingleEmailDialog(prev => ({ ...prev, isSending: true, error: '', success: '' }))
    try {
      const userId = localStorage.getItem('user_id')
      const payload = {
        emails: emails.join(','),
        order_id: order.order_id,
        user_id: userId,
        invoice: !!sendInvoice,
        delivery: !!sendDelivery
      }
      const res = await apiService.post(
        API_ENDPOINTS.POST_send_challan_on_email,
        JSON.stringify(payload),
        'application/json'
      )
      if (res?.success) {
        setSingleEmailDialog(prev => ({ ...prev, success: 'Email sent successfully', isSending: false }))
        setTimeout(() => closeSingleOrderEmail(), 1200)
      } else {
        throw new Error(res?.message || 'Failed to send email')
      }
    } catch (err) {
      setSingleEmailDialog(prev => ({ ...prev, error: err?.message || 'Failed to send email', isSending: false }))
    }
  }

  useEffect(() => {
    const fetchStatusOptions = async () => {
      try {
        const res = await apiService.get(API_ENDPOINTS.GET_order_status_choices)
        const list = Array.isArray(res?.data) ? res.data : []
        const options = list
          .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
          .map(s => ({
            id: s.id,
            value: (s.name || '').toLowerCase(),
            name: s.name || '',
            label: s.name || '',
            category: s.category || s.name || '',
            sequence: s.sequence || 0
          }))
        setStatusOptions(options)
      } catch (e) {
        console.error('Error fetching status options:', e)
      }
    }
    fetchStatusOptions()
  }, [])

  useEffect(() => {
    const fetchDispatchPersons = async () => {
      try {
        const res = await apiService.get(API_ENDPOINTS.GET_dispatch_persons)
        const list = Array.isArray(res?.data?.DispatchPerson) ? res.data.DispatchPerson : []
        const filteredList = list.filter(person => !person.is_deleted)
        setDispatchPersons(filteredList)
      } catch (e) {
        console.error('Error fetching dispatch persons:', e)
      }
    }
    fetchDispatchPersons()
  }, [])

  useEffect(() => {
    const fetchOrderByChoices = async () => {
      try {
        const res = await apiService.get(API_ENDPOINTS.GET_order_by_choices)
        if (res?.success && Array.isArray(res?.data)) {
          setOrderByChoices(res.data)
        } else {
          console.error('Invalid response format from order by choices API')
          setOrderByChoices([])
        }
      } catch (e) {
        console.error('Error fetching order by choices:', e)
        setOrderByChoices([])
      }
    }
    fetchOrderByChoices()
  }, [])

  const normalizeProductForPayment = (product) => {
    if (!product) return null
    const paymentStatusValue = getOrderPaymentStatusValue(product)
    return {
      ...product,
      total_amount: product.order?.total_amount ?? product.total_amount,
      payment_status: {
        ...(typeof product.payment_status === 'object' ? product.payment_status : {}),
        name: paymentStatusValue,
      },
      status: {
        name: product.status_name || product.status,
        category: product.status_category || product.status_name || product.status,
      },
    }
  }

  const closePaymentUpdateModalState = () => ({ ...DEFAULT_PAYMENT_UPDATE_MODAL })

  const PAYMENT_METHOD_REQUIRED_FIELDS = {
    bank_transfer: ['transaction_reference_no'],
    cash: ['receiver_name'],
    cheque: ['cheque_number'],
  }

  const enforcePaymentMethodRequiredFields = (formConfig) => {
    if (!formConfig?.fields) return formConfig
    const requiredKeys = PAYMENT_METHOD_REQUIRED_FIELDS[formConfig.type] || []
    if (!requiredKeys.length) return formConfig
    return {
      ...formConfig,
      fields: formConfig.fields.map((field) =>
        requiredKeys.includes(field.key) ? { ...field, required: true } : field
      ),
    }
  }

  const getCreditChequePaymentFields = (paymentFields, installmentAmount, paymentMethodConfig) => {
    if (paymentMethodConfig?.type !== 'cheque') return paymentFields
    return {
      ...paymentFields,
      cheque_amount: installmentAmount ?? '',
    }
  }

  const validatePaymentMethodMandatoryFields = (modalState) => {
    if (isZeroRemainingPayment(modalState)) return ''
    if (!modalState?.paymentMethod || !modalState?.paymentMethodConfig) return ''

    const fields = modalState.paymentFields || {}
    const type = modalState.paymentMethodConfig.type

    if (type === 'bank_transfer') {
      const ref = String(fields.transaction_reference_no || fields.transaction_id || '').trim()
      if (!ref) return 'Please enter Transaction Reference No.'
      if (!/^[A-Z0-9]+$/.test(ref)) {
        return 'Transaction Reference No. must contain only English letters and numbers (A–Z, 0–9)'
      }
    }
    if (type === 'cash' && !String(fields.receiver_name || '').trim()) {
      return 'Please enter Receiver Name (Staff)'
    }
    if (type === 'cheque' && !String(fields.cheque_number || '').trim()) {
      return 'Please enter Cheque Number'
    }
    return ''
  }

  const buildNewCreditPaymentFormData = (modalState, orderId, installmentAmount, creditNote = '') => {
    const paidFormState = {
      ...modalState,
      advanceAmount: String(installmentAmount),
      orderPaymentDetails: modalState?.orderPaymentDetails
        ? { ...modalState.orderPaymentDetails, remainingAmount: installmentAmount }
        : { remainingAmount: installmentAmount, totalAmount: installmentAmount, paidAmount: 0 },
    }
    const paidFormData = buildNewPaidPaymentFormData(paidFormState, orderId)
    const formData = new FormData()
    for (const [key, value] of paidFormData.entries()) {
      if (key === 'payment_status') {
        formData.append(key, 'Credit')
      } else if (key === 'paid_amount') {
        formData.append(key, String(installmentAmount))
      } else {
        formData.append(key, value)
      }
    }
    if (creditNote.trim()) {
      formData.append('note', creditNote.trim())
    }
    return formData
  }

  const getInlinePaidPaymentStepState = (orderPaymentDetails) => ({
    ...getPaidPaymentStepState(orderPaymentDetails),
    step: 'status',
  })

  const getCreditPaymentStepState = (orderPaymentDetails = null) => {
    const remainingAmount = Number(orderPaymentDetails?.remainingAmount ?? 0)
    return {
      step: 'status',
      paymentOption: 'credit',
      installmentAmount: remainingAmount === 0 ? '0' : '',
      creditNote: '',
      paymentMethod: '',
      paymentMethodConfig: null,
      paymentFields: {},
      paymentFiles: {},
      paymentError: '',
    }
  }

  const openPaymentUpdateModalState = (product) => {
    const order = normalizeProductForPayment(product)
    const paymentStatus = getOrderPaymentStatusValue(order)
    const orderDetails = getOrderPaymentDetailsFromOrder(order)
    const base = {
      ...DEFAULT_PAYMENT_UPDATE_MODAL,
      open: true,
      order,
      initialPaymentStatus: paymentStatus,
      refundAmount:
        product?.refund_amount ??
        product?.refunded_amount ??
        product?.order?.total_amount ??
        product?.total_amount ??
        '',
    }

    if (paymentStatus === 'credit') {
      return {
        ...base,
        orderPaymentDetails: orderDetails,
        ...getCreditPaymentStepState(orderDetails),
      }
    }

    return base
  }

  const applyOrderPaymentDetails = (orderId, orderDetails) => {
    const currentStatus = orderDetails?.current_payment_status
      ? String(orderDetails.current_payment_status).toLowerCase()
      : null
    const paidStepPatch = getPaidPaymentStepState(orderDetails)

    setPaymentUpdateModal((prev) => {
      if (!prev.open || prev.order?.order_id !== orderId) return prev
      return {
        ...prev,
        orderPaymentDetails: orderDetails,
        ...(currentStatus
          ? {
              order: {
                ...prev.order,
                payment_status: {
                  ...(typeof prev.order?.payment_status === 'object' ? prev.order.payment_status : {}),
                  name: currentStatus,
                },
              },
            }
          : {}),
        ...(currentStatus === 'credit' || prev.paymentOption === 'credit'
          ? { ...getCreditPaymentStepState(orderDetails), step: 'status' }
          : prev.paymentOption === 'remaining'
            ? { ...paidStepPatch, step: 'status' }
            : {}),
      }
    })

    setBulkPaymentStatusModal((prev) => {
      if (!prev.open || prev.summaryOrder?.order_id !== orderId) return prev
      return {
        ...prev,
        orderPaymentDetails: orderDetails,
        ...(currentStatus === 'credit' || prev.paymentOption === 'credit'
          ? { ...getCreditPaymentStepState(orderDetails), step: 'status' }
          : prev.paymentOption === 'remaining'
            ? { ...paidStepPatch, step: 'status', selectedPaymentStatus: prev.selectedPaymentStatus || 'paid' }
            : prev.step === 'payment_details'
              ? {
                  ...paidStepPatch,
                  selectedPaymentStatus: prev.selectedPaymentStatus || 'paid',
                }
              : {}),
      }
    })
  }

  const fetchPaymentStatusChoicesForOrder = async (orderId) => {
    if (!orderId) {
      setPaymentStatusChoices([])
      return
    }
    setLoadingPaymentStatusChoices(true)
    try {
      const res = await apiService.get(
        `${API_ENDPOINTS.GET_payment_status_choices}?order_id=${encodeURIComponent(orderId)}`
      )
      if (res?.success && Array.isArray(res?.data)) {
        setPaymentStatusChoices(
          [...res.data].sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
        )
        applyOrderPaymentDetails(orderId, res?.order_details || null)
      } else {
        setPaymentStatusChoices([])
        applyOrderPaymentDetails(orderId, null)
      }
    } catch (e) {
      console.error('Error fetching payment status choices:', e)
      setPaymentStatusChoices([])
      applyOrderPaymentDetails(orderId, null)
    } finally {
      setLoadingPaymentStatusChoices(false)
    }
  }

  const isPaymentUpdateDisabled = (product) => {
    const order = normalizeProductForPayment(product)
    const paymentStatus = getOrderPaymentStatusValue(order)
    if (paymentStatus === 'paid') return true
    if (paymentStatus === 'credit') return false
    return isPaymentUpdateDisabledForOrder(order, statusFilter)
  }

  const getPaymentUpdateTooltip = (product) => {
    if (!shouldEnableButton?.('update_payment')) {
      return "You don't have permission to update payment"
    }
    const order = normalizeProductForPayment(product)
    const paymentStatus = getOrderPaymentStatusValue(order)
    if (paymentStatus === 'paid') {
      return 'Payment status cannot be updated when the order is already paid'
    }
    if (paymentStatus === 'credit') {
      return 'Update Payment'
    }
    const reason = getPaymentUpdateDisabledReason(order, statusFilter)
    if (reason) return reason
    return 'Update Payment'
  }

  const loadAdvancePaymentMethods = async () => {
    setLoadingAdvancePaymentMethods(true)
    try {
      const methods = await fetchAdvancePaymentMethods()
      setAdvancePaymentMethods(methods)
    } catch (error) {
      console.error('Error fetching advance payment methods:', error)
      setAdvancePaymentMethods([])
    } finally {
      setLoadingAdvancePaymentMethods(false)
    }
  }

  const buildPaymentMethodChangePayload = (methodId, orderPaymentDetails) => {
    const config = paymentMethodsById[methodId] || null
    const remaining = Number(orderPaymentDetails?.remainingAmount ?? 0)
    return {
      paymentMethod: methodId,
      paymentMethodConfig: config,
      paymentFields:
        config?.type === 'cheque' && remaining > 0
          ? { cheque_amount: String(remaining) }
          : {},
      paymentFiles: {},
      paymentError: '',
    }
  }

  const openPaymentModalForProduct = (product) => {
    if (!product || isPaymentUpdateDisabled(product)) return
    setPaymentUpdateModal(openPaymentUpdateModalState(product))
    fetchPaymentStatusChoicesForOrder(product.order_id)
    loadAdvancePaymentMethods()
  }

  const getPaymentChoiceValue = (choice) =>
    String(choice.value || choice.key || choice.name || '').toLowerCase()

  const getPaymentChoiceLabel = (choice) =>
    choice.label || choice.name || choice.value || choice.key || ''

  const paymentStatusFallbackOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'outstanding', label: 'Outstanding' },
    { value: 'paid', label: 'Paid' },
    { value: 'failed', label: 'Failed' },
    { value: 'refunded', label: 'Refunded' },
  ]

  const renderPaymentStatusMenuItems = ({ hidePaid = false } = {}) => {
    const shouldHidePaid = (value) =>
      hidePaid && String(value || '').toLowerCase() === 'paid'

    if (loadingPaymentStatusChoices) {
      return (
        <MenuItem disabled>
          <CircularProgress size={18} sx={{ mr: 1 }} />
          Loading payment statuses...
        </MenuItem>
      )
    }
    if (paymentStatusChoices.length > 0) {
      return paymentStatusChoices
        .filter((choice) => !shouldHidePaid(getPaymentChoiceValue(choice)))
        .map((choice) => {
        const value = getPaymentChoiceValue(choice)
        const label = getPaymentChoiceLabel(choice)
        return (
          <MenuItem key={value || label} value={value}>
            <PaymentStatusChip label={label} status={value} size="small" sx={{ mr: 1 }} />
            {label}
          </MenuItem>
        )
      })
    }
    return paymentStatusFallbackOptions
      .filter((option) => !shouldHidePaid(option.value))
      .map((option) => (
      <MenuItem key={option.value} value={option.value}>
        <PaymentStatusChip label={option.label} status={option.value} size="small" sx={{ mr: 1 }} />
        {option.label}
      </MenuItem>
    ))
  }

  const validatePaidPaymentDetails = (modalState) => {
    const apiValidation = validateNewPaidPaymentRequest(modalState)
    if (apiValidation) return apiValidation
    const mandatoryError = validatePaymentMethodMandatoryFields(modalState)
    if (mandatoryError) return mandatoryError
    return validatePaidPaymentFormDetails(
      modalState,
      enforcePaymentMethodRequiredFields(modalState.paymentMethodConfig)
    )
  }

  const canSubmitPaidPayment = (modalState) => {
    if (modalState?.paymentOption === 'credit') return false
    if (!modalState?.orderPaymentDetails) return false
    if (isZeroRemainingPayment(modalState)) return true
    return !!(modalState.paymentMethod && modalState.paymentMethodConfig)
  }

  const canSubmitCreditPayment = (modalState) => {
    if (modalState?.paymentOption !== 'credit') return false
    const amount = Number(modalState.installmentAmount)
    const remainingAmount = Number(modalState?.orderPaymentDetails?.remainingAmount ?? 0)
    if (remainingAmount === 0) return true
    const installmentValid =
      modalState.installmentAmount !== '' &&
      !Number.isNaN(amount) &&
      amount > 0 &&
      amount <= remainingAmount
    if (!installmentValid) return false
    if (!modalState.paymentMethod || !modalState.paymentMethodConfig) return false
    return !validatePaymentMethodMandatoryFields(modalState)
  }

  const shouldShowCreditSubmitButton = (modalState) => {
    if (modalState?.paymentOption !== 'credit') return false
    const remainingAmount = Number(modalState?.orderPaymentDetails?.remainingAmount ?? 0)
    return remainingAmount >= 0
  }

  const submitPaymentStatusRequest = async (order_id, newStatus, extras = {}) => {
    const normalizedStatus = String(newStatus || '').toLowerCase()

    if (normalizedStatus === 'paid') {
      if (!extras.modalState) {
        throw { success: false, message: 'Payment details are required' }
      }
      return submitNewPaidPaymentStatus(extras.modalState, order_id)
    }

    const userId = localStorage.getItem('user_id')
    const { refundAmount, orderTotalAmount, installmentAmount, creditNote } = extras
    const isRefunded = normalizedStatus === 'refunded'
    const isCredit = normalizedStatus === 'credit'
    const refundVal =
      refundAmount != null && refundAmount !== '' ? refundAmount : orderTotalAmount ?? 0

    const payload = {
      order_id,
      payment_status: newStatus,
      user_id: userId,
      ...(isRefunded && { refund_amount: refundVal }),
      ...(isCredit && installmentAmount != null && { installment_amount: installmentAmount }),
      ...(isCredit && creditNote && { note: creditNote }),
    }
    return apiService.put(API_ENDPOINTS.UPDATE_payment_status, payload)
  }

  const getPaymentStatusColor = (statusName) => {
    const status = (statusName || '').toLowerCase()
    switch (status) {
      case 'pending': return 'warning'
      case 'paid': return 'success'
      case 'failed': return 'error'
      case 'refunded': return 'info'
      default: return 'default'
    }
  }

  const toggleRow = (id) => {
    const newExpandedRows = new Set(expandedRows)
    if (expandedRows.has(id)) {
      newExpandedRows.delete(id)
    } else {
      newExpandedRows.add(id)
    }
    setExpandedRows(newExpandedRows)
  }

  const formatBulkProductData = (bulkProduct) => {
    if (!bulkProduct) return {}
    
    const statusNameRaw = bulkProduct.status?.name || (typeof bulkProduct.status === 'string' ? bulkProduct.status : '')
    const statusCategoryRaw = bulkProduct.status?.category || statusNameRaw
    const statusName = (statusNameRaw || '').toString().trim()
    const statusCategory = (statusCategoryRaw || '').toString().trim()
    const normalizedStatus = statusName.toLowerCase() || 'pending'

    const paymentStatusNameRaw = bulkProduct.payment_status?.name || (typeof bulkProduct.payment_status === 'string' ? bulkProduct.payment_status : '')
    const paymentStatusName = (paymentStatusNameRaw || '').toString().trim()
    const normalizedPaymentStatus = paymentStatusName.toLowerCase() || 'pending'
    const quantity = Number(bulkProduct.quantity || 0)
    const unitPrice = Number(bulkProduct.discounted_price || 0)
    const orderSubtotal = Number(bulkProduct.order?.subtotal || (unitPrice * quantity) || 0)
    const orderGstAmount = Number(bulkProduct.order?.gst_amount || 0)
    const orderDeliveryCharge = Number(bulkProduct.order?.delivery_charge || 0)
    const orderDiscountAmount = Number(bulkProduct.order?.discount_amount || 0)
    const orderTotalAmount = Number(bulkProduct.order?.total_amount || orderSubtotal || 0)
    
    return {
      id: bulkProduct.id,
      order_id: bulkProduct.order?.order_id || bulkProduct.order?.id,
      product_name: bulkProduct.product?.name || 'N/A',
      sku: bulkProduct.product?.sku || 'N/A',
      quantity: bulkProduct.quantity || 0,
      uom: bulkProduct.uom || 'pcs',
      discounted_price: parseFloat(bulkProduct.discounted_price?.replace('₹', '').trim() || 0).toFixed(2),
      category: bulkProduct.category || 'N/A',
      company_name:
        bulkProduct.company_name ||
        bulkProduct.company?.name ||
        bulkProduct.order?.company_name ||
        'N/A',
      category_name:
        bulkProduct.category_name ||
        bulkProduct.product?.category_name ||
        'N/A',
      source: bulkProduct.source || 'N/A',
      status: normalizedStatus, // normalized key for internal logic
      status_name: statusName, // display name (e.g., "Shipped")
      status_category: statusCategory, // drives icon/color (e.g., "Shipped")
      payment_status: normalizedPaymentStatus,
      payment_status_name: paymentStatusName,
      created_at: bulkProduct.created_at || new Date().toISOString(),
      expected_delivery_date: bulkProduct.order?.expected_delivery_date || bulkProduct.expected_delivery_date || null,
      order_date_formatted: bulkProduct.order?.order_date_formatted || bulkProduct.order_date_formatted || null,
      customer_name: bulkProduct.end_user?.full_name || bulkProduct.customer_name || 'N/A',
      mobile_number: bulkProduct.end_user?.mobile_number || bulkProduct.mobile_number || 'N/A',
      alternate_number: bulkProduct.alternate_number || '',
      email: bulkProduct.end_user?.email || bulkProduct.email || '',
      gst_no: bulkProduct.gst_no || '',
      description: bulkProduct.description || 'No description available',
      reason: bulkProduct.order?.reason || bulkProduct.reason || '',
      product_image: bulkProduct.product?.image || bulkProduct.image_url || '',
      // Invoice information
      invoice: bulkProduct.invoice || null,
      // Delivery challan information
      delivery_challan: bulkProduct.delivery_challan || null,
      advance_payment: bulkProduct.advance_payment || null,
      // Keep nested order details available for rich expanded view cards.
      order: {
        id: bulkProduct.order?.id || null,
        order_id: bulkProduct.order?.order_id || bulkProduct.order?.id || null,
        order_date_formatted: bulkProduct.order?.order_date_formatted || bulkProduct.order_date_formatted || null,
        delivery_date_formatted: bulkProduct.order?.delivery_date_formatted || null,
        delivery_date: bulkProduct.order?.delivery_date || null,
        expected_delivery_date: bulkProduct.order?.expected_delivery_date || null,
        order_by: bulkProduct.order?.order_by || null,
        order_by_entity: bulkProduct.order?.order_by_entity || null,
        notes: bulkProduct.order?.notes || null,
        subtotal: orderSubtotal,
        gst_amount: orderGstAmount,
        delivery_charge: orderDeliveryCharge,
        discount_amount: orderDiscountAmount,
        total_amount: orderTotalAmount,
        total_items: 1,
        total_quantity: quantity
      },
      // Address details
      address: bulkProduct.end_user ? {
        pincode: bulkProduct.end_user.pincode,
        country: bulkProduct.end_user.country,
        state: bulkProduct.end_user.state,
        district: bulkProduct.end_user.district,
        tehsil: bulkProduct.end_user.tehsil,
        village: bulkProduct.end_user.village
      } : null
  }
}

  const formatCurrency = (value) => {
    const amount = Number(value || 0)
    return `₹${Number.isFinite(amount) ? amount.toFixed(2) : '0.00'}`
  }

  const normalizedData = bulkProducts.map(formatBulkProductData)
  
  const filteredData = normalizedData
    .filter(product => {
      const query = (searchQuery || '').toString().trim().toLowerCase()
      const matchesSearch =
        !query ||
        (product.product_name || '').toString().trim().toLowerCase().includes(query) ||
        (product.customer_name || '').toString().trim().toLowerCase().includes(query) ||
        (product.order_id || '').toString().trim().toLowerCase().includes(query)
      return matchesSearch
    })

  const getTruckOrderWithStartKm = (orders = []) =>
    orders.find((order) => {
      const challan = order?.delivery_challan || {};
      const orderBy = String(order?.order?.order_by || challan?.order_by || '')
        .trim()
        .toLowerCase();
      const startKm = challan?.start_km;
      return (
        (orderBy === 'truck' || orderBy === 'by truck') &&
        startKm !== null &&
        startKm !== undefined &&
        String(startKm).trim() !== ''
      );
    });

  const isBulkPaymentUpdateDisabled = () => {
    if (PAYMENT_UPDATE_LOCKED_ORDER_STATUSES.has(
      String(statusFilter || '').toLowerCase().trim()
    )) {
      return true
    }
    const selected = filteredData.filter((product) => selectedProducts.includes(product.id))
    if (selected.length === 0) return true
    return selected.every((product) => isPaymentUpdateDisabled(product))
  }

  // Determine if bulk submit should be disabled (mirror single-order status rules)
  const isBulkSubmitDisabled = () => {
    if (bulkChallanModal.isCreating) return true
    const selectedStatus = bulkChallanModal.selectedStatus
    if (!selectedStatus || !selectedStatus.trim()) return true

    // Payment check - if any selected order unpaid, only 'Orders' allowed
    const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id))
    const hasUnpaidOrders = selectedOrders.some(order => {
      const paymentStatus = (order.payment_status?.name || order.payment_status || '').toLowerCase()
      return !(paymentStatus === 'paid' || paymentStatus === 'outstanding')
    })
    if (hasUnpaidOrders && selectedStatus !== 'Orders') return true

    const selCat = (bulkChallanModal.selectedStatusCategory || '').toLowerCase()

    // If Dispatch / Shipped - require orderBy, entityName, driver, vehicle and dispatch person
    if (selCat === 'shipped' || selectedStatus === 'Dispatch') {
      const dispatchPersonName = bulkChallanModal.useCustomDispatchPerson
        ? bulkChallanModal.customDispatchPerson
        : bulkChallanModal.dispatchPersonName
      
      // Check if Start KM is required (when Order By is "Truck")
      const isTruckOrder = String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck';
      const hasStartKm = isTruckOrder ? String(bulkChallanModal.start_km || '').trim() !== '' : true;
      const hasValidStartKm = isTruckOrder ? Number(bulkChallanModal.start_km) >= 1 : true;
      
      if (
        !bulkChallanModal.orderBy?.trim() ||
        !bulkChallanModal.entityName?.trim() ||
        !bulkChallanModal.driverName?.trim() ||
        (isTruckOrder && !bulkChallanModal.vehicleNo?.trim()) ||
        (isTruckOrder && isVehicleReferenceNoInvalid(bulkChallanModal.vehicleNo)) ||
        !hasStartKm ||
        !hasValidStartKm ||
        !dispatchPersonName?.trim()
      ) {
        return true
      }
    }

    // Delivered - only require / validate End KM for Truck orders with Start KM
    if (selectedStatus === 'Delivered') {
      const truckOrderWithStartKm = getTruckOrderWithStartKm(selectedOrders)

      if (truckOrderWithStartKm) {
        const startKm = parseFloat(bulkChallanModal.start_km || truckOrderWithStartKm.delivery_challan?.start_km)
        const endKm = parseFloat(bulkChallanModal.end_km)
        if (!String(bulkChallanModal.end_km || '').trim()) return true
        if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) return true
      }
    }

    // Cancelled / Returned require reason
    if (selectedStatus === 'Cancelled' && !bulkChallanModal.cancellationReason?.trim()) return true
    if (selectedStatus === 'Returned' && !bulkChallanModal.returnReason?.trim()) return true

    return false
  }

  // Create delivery challan
  const createDeliveryChallan = async (orderId, driverName, vehicleNo) => {
    try {
      const formData = new FormData();
      formData.append('order_id', orderId);
      formData.append('driver_name', driverName);
      formData.append('vehicle_no', vehicleNo);
      if (statusUpdateModal.shippedDate) {
        formData.append('delivery_date', statusUpdateModal.shippedDate);
      }
      formData.append('invoice', statusUpdateModal.generateInvoice ? 'true' : 'false');
      if (statusUpdateModal.emails?.length > 0) {
        formData.append('emails', statusUpdateModal.emails.join(','));
      }
      // Add dispatch person name
      const dispatchPersonName = statusUpdateModal.useCustomDispatchPerson 
        ? statusUpdateModal.customDispatchPerson 
        : statusUpdateModal.dispatchPersonName;
      if (dispatchPersonName) {
        formData.append('dispatch_person_name', dispatchPersonName);
      }
      // Add status to delivery challan creation
      if (statusUpdateModal.order?.status_name) {
        formData.append('status', statusUpdateModal.order.status_name);
      }
      // Add order_by if provided
      if (statusUpdateModal.orderBy) {
        formData.append('order_by', statusUpdateModal.orderBy);
      }
      // Add order_by_entity if provided
      if (statusUpdateModal.entityName) {
        formData.append('order_by_entity', statusUpdateModal.entityName);
      }

      if (statusUpdateModal.start_km) {
        formData.append('start_km', statusUpdateModal.start_km)
      }
      
      const response = await apiService.post(
        API_ENDPOINTS.POST_create_delivery_challan,
        formData,
        "multipart/form-data"
      );

      return response;
    } catch (error) {
      console.error('Error creating delivery challan:', error);
      throw error;
    }
  };

  const handleStatusUpdate = async (id, statusName, orderId) => {
    try {
      console.log('Updating status for order:', orderId);
      console.log('New status (name):', statusName);

      setUpdatingStatus(true);
      
      // Validate payment status - allow orders to proceed if paid or outstanding
      // Outstanding payment status is allowed to proceed (no restriction)
      const paymentStatus = (statusUpdateModal.order?.payment_status?.name || statusUpdateModal.order?.payment_status || '').toLowerCase();
      const isPaid = paymentStatus === 'paid' || paymentStatus === 'outstanding' || paymentStatus === 'credit';
      const isOrdersStatus = statusName === 'Orders';
      
      // Only block if payment is not paid/outstanding/credit AND status is not "Orders"
      // Outstanding and Credit payment status allow orders to proceed
      if (!isPaid && !isOrdersStatus) {
        setStatusUpdateModal(prev => ({
          ...prev,
          challanError: 'This order must be paid before it can be moved to any status other than "Orders". Please update the payment status first.'
        }));
        setUpdatingStatus(false);
        return;
      }
      
      // Validate sequential status update
      // Use originalStatus if available (before user changes), otherwise use current order status
      const originalStatusCategory = (statusUpdateModal.originalStatus?.category || '').toLowerCase();
      const originalStatusName = (statusUpdateModal.originalStatus?.name || '').toLowerCase();
      const currentStatusCategory = originalStatusCategory || (statusUpdateModal.order?.status_category || statusUpdateModal.order?.status_name || '').toLowerCase();
      const currentStatusName = originalStatusName || (statusUpdateModal.order?.status_name || statusUpdateModal.order?.status?.name || '').toLowerCase();
      const selectedOption = statusOptions.find(opt => opt.name === statusName);
      const newStatusCategory = (selectedOption?.category || '').toLowerCase();
      const newStatusName = (statusName || '').toLowerCase();
      
      const statusOrder = getStatusOrder();
      const currentOrder = statusOrder[currentStatusCategory] || 0;
      const newOrder = statusOrder[newStatusCategory] || 0;
      const processingOrder = statusOrder['processing'] || 0;
      const deliveredOrder = statusOrder['delivered'] || 999;
      
      // Check if current status is Processing
      const isProcessingStatus = currentStatusName === 'processing' || 
                                  currentStatusCategory === 'processing' ||
                                  currentOrder === processingOrder;
      
      // Check if current status is Delivered
      const isDeliveredStatus = currentStatusName === 'delivered' || 
                                currentStatusCategory === 'delivered' ||
                                currentOrder === deliveredOrder;
      
      // Check if current status is Cancelled
      const isCancelledStatus = currentStatusName === 'cancelled' || 
                                currentStatusCategory === 'cancelled';
      
      const isCancelledOrReturned = newStatusCategory === 'cancelled' || newStatusCategory === 'returned' ||
                                    newStatusName === 'cancelled' || newStatusName === 'returned';
      const isReturned = newStatusCategory === 'returned' || newStatusName === 'returned';
      const isDispatch = newStatusCategory === 'shipped' || newStatusCategory === 'dispatch' || newStatusName === 'dispatch';
      
      // Special case: If status is Delivered, only allow Returned
      if (isDeliveredStatus) {
        if (!isReturned) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: `When an order is Delivered, only Returned status is allowed.`
          }));
          setUpdatingStatus(false);
          return;
        }
        // Allow Returned from Delivered, skip other validations
      } else {
        // Returned is only available from Delivered status
        if (isReturned) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: `Returned status is only available when the order is Delivered.`
          }));
          setUpdatingStatus(false);
          return;
        }
        
        // Special case: If status is Cancelled, no further status changes allowed
        if (isCancelledStatus) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: `Cancelled is a final status. No further status changes are allowed.`
          }));
          setUpdatingStatus(false);
          return;
        }
        
        // Dispatch is only available when current status is Processing
        // This check ensures the order is in Processing before allowing Dispatch
        // The sequential flow check below will also validate the order progression
        if (isDispatch) {
          // Get dispatch order to verify it's the next step after Processing
          const dispatchOrder = statusOrder['shipped'] || statusOrder['dispatch'] || 999;
          
          // Check if current status is actually Processing
          // Verify by name, category, and order sequence
          const isActuallyProcessing = (currentStatusName === 'processing' || 
                                      currentStatusCategory === 'processing') &&
                                      currentOrder === processingOrder &&
                                      processingOrder > 0;
          
          if (!isActuallyProcessing) {
            // Use original status for display, fallback to current if original not available
            const currentStatusDisplay = statusUpdateModal.originalStatus?.name || 
                                       statusUpdateModal.order?.status_name || 
                                       statusUpdateModal.order?.status?.name || 
                                       currentStatusCategory ||
                                       'Unknown';
            setStatusUpdateModal(prev => ({
              ...prev,
              challanError: `Dispatch status is only available when the order is in Processing status. Current status: ${currentStatusDisplay}.`
            }));
            setUpdatingStatus(false);
            return;
          }
        }
        
        // Cancelled is allowed before Delivered (but not after, which is handled by isDeliveredStatus check above)
        // The sequential flow validation below will handle if Cancelled can be selected
      }
      
      // For non-cancelled/returned statuses, enforce sequential flow
      if (!isCancelledOrReturned) {
        // Check if trying to skip sequential steps
        if (newOrder > currentOrder + 1) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: `Status must be updated sequentially. You can only move to the next status in the sequence. Current: ${statusUpdateModal.order?.status_name || currentStatusCategory}, Next allowed: ${statusOptions.find(opt => {
              const optOrder = statusOrder[(opt.category || '').toLowerCase()] || 0;
              return optOrder === currentOrder + 1;
            })?.name || 'N/A'}.`
          }));
          setUpdatingStatus(false);
          return;
        }
        
        // Check if trying to go backwards
        if (newOrder < currentOrder) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: `Cannot move to a previous status. Current status: ${statusUpdateModal.order?.status_name || currentStatusCategory}.`
          }));
          setUpdatingStatus(false);
          return;
        }
      }
      
      // If status category is 'shipped' (Dispatch), create delivery challan first
      const selectedOptionForShipping = statusOptions.find(opt => opt.name === statusName);
      const statusCategoryForShipping = (selectedOptionForShipping?.category || '').toLowerCase();
      if (statusCategoryForShipping === 'shipped') {
        const { driverName, vehicleNo } = statusUpdateModal;
        const isTruckOrderBy = String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck';
        
        // Validate required fields for shipping
        if (!statusUpdateModal.orderBy?.trim() || !statusUpdateModal.entityName?.trim() || !driverName.trim() || !statusUpdateModal.shippedDate || (isTruckOrderBy && !vehicleNo.trim())) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: 'Order By, Entity Name, Driver name, vehicle number, and Delivery Date are required for shipping'
          }));
          setUpdatingStatus(false);
          return;
        }

        if (isTruckOrderBy && isVehicleReferenceNoInvalid(vehicleNo)) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: 'Vehicle No / Reference No must be in format MH 12 AB 1234'
          }));
          setUpdatingStatus(false);
          return;
        }
        
        // Validate dispatch person (either selected or custom)
        const dispatchPersonName = statusUpdateModal.useCustomDispatchPerson 
          ? statusUpdateModal.customDispatchPerson 
          : statusUpdateModal.dispatchPersonName;
        if (!dispatchPersonName?.trim()) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: 'Dispatch Person Name is required. Please select a dispatch person or add a custom one.'
          }));
          setUpdatingStatus(false);
          return;
        }

        setStatusUpdateModal(prev => ({
          ...prev,
          isCreatingChallan: true,
          challanError: ''
        }));

        try {
          // Create delivery challan first
          const challanResponse = await createDeliveryChallan(orderId, driverName, vehicleNo);
          
          if (!challanResponse?.success) {
            throw new Error(challanResponse?.message || 'Failed to create delivery challan');
          }

          console.log('Delivery challan created successfully');
        } catch (challanError) {
          console.error('Error creating delivery challan:', challanError);
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: challanError.message || 'Failed to create delivery challan',
            isCreatingChallan: false
          }));
          setUpdatingStatus(false);
          return;
        }

        setStatusUpdateModal(prev => ({
          ...prev,
          isCreatingChallan: false
        }));
      }

      if (statusName === 'Delivered') {
        const truckOrderWithStartKm = getTruckOrderWithStartKm([statusUpdateModal.order]);
        const deliveryChallan = truckOrderWithStartKm?.delivery_challan;
        if (deliveryChallan) {
          const startKm = parseFloat(statusUpdateModal.start_km || deliveryChallan.start_km);
          const endKm = parseFloat(statusUpdateModal.end_km);
          if (!String(statusUpdateModal.end_km || '').trim()) {
            setStatusUpdateModal(prev => ({
              ...prev,
              challanError: 'End KM is required'
            }));
            setUpdatingStatus(false);
            return;
          }
          if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) {
            setStatusUpdateModal(prev => ({
              ...prev,
              challanError: 'End KM must be greater than Start KM'
            }));
            setUpdatingStatus(false);
            return;
          }
        }
      }

      // Now update the status
      const userId = localStorage.getItem("user_id");
      const dispatchPersonName = statusUpdateModal.useCustomDispatchPerson 
        ? statusUpdateModal.customDispatchPerson 
        : statusUpdateModal.dispatchPersonName;
      const payload = {
        user_id: userId,
        status: statusName, // send Name as requested
        order_id: orderId,
        emails: statusUpdateModal.emails,
        generateInvoice: statusUpdateModal.generateInvoice,
        driverName: statusUpdateModal.driverName,
        vehicleNo: statusUpdateModal.vehicleNo,
        delivery_date: statusName === 'Processing' ? null : statusUpdateModal.shippedDate,
        dispatch_person_name: dispatchPersonName,
        start_km: statusUpdateModal.start_km,  // new
        ...(statusUpdateModal.orderBy && { order_by: statusUpdateModal.orderBy }),
        ...(statusUpdateModal.entityName && { order_by_entity: statusUpdateModal.entityName }),
        ...(statusName === 'Delivered' && { end_km: statusUpdateModal.end_km }),
        ...(statusName === 'Cancelled' && { reason: statusUpdateModal.cancellationReason || '' }),
        ...(statusName === 'Returned' && { reason: statusUpdateModal.returnReason || '' })
      };

      const response = await apiService.put(
        API_ENDPOINTS.UPDATE_product_status,
        payload,
        "application/json"
      );

      if (response?.success) {
        setStatusUpdateModal({ 
          open: false, 
          order: null,
          originalStatus: null,
          driverName: '',
          vehicleNo: '',
          isCreatingChallan: false,
          challanError: '',
          emails: [],
          emailInput: '',
          dispatchPersonName: '',
          customDispatchPerson: '',
          useCustomDispatchPerson: false,
          start_km: '',
          end_km: '',
          cancellationReason: '',
          returnReason: ''
        });
        // Clear selections and refresh data
        if (onSelectAll) {
          onSelectAll(false);
        }
        setTimeout(() => {
          onRefresh?.();
        }, 100);
      } else {
        console.error('Status update failed:', response?.message);
        setStatusUpdateModal(prev => ({
          ...prev,
          challanError: response?.message || 'Failed to update status'
        }));
      }
    } catch (response) {
      console.error('Error updating status:', response);
      setStatusUpdateModal(prev => ({
        ...prev,
        challanError: response?.errors?.join(', ') || 'Failed to update status'
      }));
      throw new Error(response?.errors?.join(', ') || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handlePaymentStatusUpdate = async (
    orderId,
    newStatus,
    order_id,
    refundAmount,
    orderTotalAmount,
    paidPaymentExtras = null
  ) => {
    setUpdatingPayment(true)
    try {
      const response = await submitPaymentStatusRequest(order_id, newStatus, {
        refundAmount,
        orderTotalAmount,
        installmentAmount: paidPaymentExtras?.installmentAmount,
        creditNote: paidPaymentExtras?.creditNote,
        modalState: paymentUpdateModal,
      })
      if (response?.success) {
        toast.showSuccess(response?.message || 'Payment status updated successfully')
        await onRefresh?.()
        setPaymentUpdateModal(closePaymentUpdateModalState())
        setPaymentStatusChoices([])
      } else {
        const message = response?.message || 'Failed to update payment status'
        toast.showError(message)
        setPaymentUpdateModal((prev) => ({ ...prev, paymentError: message }))
      }
    } catch (error) {
      console.error('Error updating payment status:', error)
      const message = extractPaymentApiError(error)
      toast.showError(message)
      setPaymentUpdateModal((prev) => ({ ...prev, paymentError: message }))
    } finally {
      setUpdatingPayment(false)
    }
  }

  const handleConfirmCreditPaymentDetails = async () => {
    const remainingAmount = Number(
      paymentUpdateModal.orderPaymentDetails?.remainingAmount ?? 0
    )
    const amount = Number(paymentUpdateModal.installmentAmount)

    if (remainingAmount > 0) {
      if (
        paymentUpdateModal.installmentAmount === '' ||
        Number.isNaN(amount) ||
        amount <= 0
      ) {
        setPaymentUpdateModal((prev) => ({
          ...prev,
          paymentError: 'Please enter a valid installment amount',
        }))
        return
      }
      if (amount > remainingAmount) {
        setPaymentUpdateModal((prev) => ({
          ...prev,
          paymentError: 'Installment amount cannot exceed the remaining amount',
        }))
        return
      }
      if (!paymentUpdateModal.paymentMethod || !paymentUpdateModal.paymentMethodConfig) {
        setPaymentUpdateModal((prev) => ({
          ...prev,
          paymentError: 'Please select a payment method',
        }))
        return
      }
    }

    const methodValidationError = validatePaymentMethodMandatoryFields(paymentUpdateModal)
    if (methodValidationError) {
      setPaymentUpdateModal((prev) => ({ ...prev, paymentError: methodValidationError }))
      return
    }

    if (!canSubmitCreditPayment(paymentUpdateModal)) {
      return
    }

    const orderId = paymentUpdateModal.order?.order_id
    const installmentAmount = remainingAmount === 0 ? 0 : Number(paymentUpdateModal.installmentAmount)
    const creditNote = paymentUpdateModal.creditNote || ''

    setUpdatingPayment(true)
    setPaymentUpdateModal((prev) => ({ ...prev, paymentError: '' }))

    try {
      const formData = buildNewCreditPaymentFormData(
        paymentUpdateModal,
        orderId,
        installmentAmount,
        creditNote
      )

      const response = await apiService.putMultipart(
        API_ENDPOINTS.UPDATE_new_payment_status,
        formData
      )

      if (response?.success) {
        toast.showSuccess(response?.message || 'Payment status updated successfully')
        await onRefresh?.()
        setPaymentUpdateModal(closePaymentUpdateModalState())
        setPaymentStatusChoices([])
      } else {
        const message = response?.message || 'Failed to update payment status'
        toast.showError(message)
        setPaymentUpdateModal((prev) => ({ ...prev, paymentError: message }))
      }
    } catch (error) {
      console.error('Error submitting credit payment:', error)
      const message = extractPaymentApiError(error)
      toast.showError(message)
      setPaymentUpdateModal((prev) => ({ ...prev, paymentError: message }))
    } finally {
      setUpdatingPayment(false)
    }
  }

  const handleConfirmPaidPaymentDetails = async () => {
    const validationError = validatePaidPaymentDetails(paymentUpdateModal)
    if (validationError) {
      setPaymentUpdateModal((prev) => ({ ...prev, paymentError: validationError }))
      return
    }

    const orderId = paymentUpdateModal.order?.order_id
    setUpdatingPayment(true)
    setPaymentUpdateModal((prev) => ({ ...prev, paymentError: '' }))

    try {
      const response = await submitNewPaidPaymentStatus(paymentUpdateModal, orderId)
      if (response?.success) {
        toast.showSuccess(response?.message || 'Payment status updated successfully')
        await onRefresh?.()
        setPaymentUpdateModal(closePaymentUpdateModalState())
        setPaymentStatusChoices([])
      } else {
        const message = response?.message || 'Failed to update payment status'
        toast.showError(message)
        setPaymentUpdateModal((prev) => ({ ...prev, paymentError: message }))
      }
    } catch (error) {
      console.error('Error submitting paid payment:', error)
      const message = extractPaymentApiError(error)
      toast.showError(message)
      setPaymentUpdateModal((prev) => ({ ...prev, paymentError: message }))
    } finally {
      setUpdatingPayment(false)
    }
  }

  const getSelectedOrdersForBulkPayment = () =>
    filteredData.filter((product) => selectedProducts.includes(product.id))

  const getUnpaidOrdersFromList = (orders = []) =>
    orders.filter((product) => {
      const paymentStatus = getOrderPaymentStatusValue(normalizeProductForPayment(product))
      return paymentStatus !== 'paid' && paymentStatus !== 'outstanding'
    })

  const handleConfirmBulkCreditPaymentDetails = async () => {
    const bulkCreditState = {
      ...bulkPaymentStatusModal,
      order: bulkPaymentStatusModal.summaryOrder,
    }
    const remainingAmount = Number(
      bulkPaymentStatusModal.orderPaymentDetails?.remainingAmount ?? 0
    )
    const amount = Number(bulkPaymentStatusModal.installmentAmount)

    if (remainingAmount > 0) {
      if (
        bulkPaymentStatusModal.installmentAmount === '' ||
        Number.isNaN(amount) ||
        amount <= 0
      ) {
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          paymentError: 'Please enter a valid installment amount',
        }))
        return
      }
      if (amount > remainingAmount) {
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          paymentError: 'Installment amount cannot exceed the remaining amount',
        }))
        return
      }
      if (!bulkPaymentStatusModal.paymentMethod || !bulkPaymentStatusModal.paymentMethodConfig) {
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          paymentError: 'Please select a payment method',
        }))
        return
      }
    }

    const bulkMethodValidationError = validatePaymentMethodMandatoryFields(bulkCreditState)
    if (bulkMethodValidationError) {
      setBulkPaymentStatusModal((prev) => ({
        ...prev,
        paymentError: bulkMethodValidationError,
      }))
      return
    }

    if (!canSubmitCreditPayment(bulkCreditState)) {
      return
    }

    const unpaidOrders = getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment())
    if (unpaidOrders.length === 0) {
      setBulkPaymentStatusModal((prev) => ({
        ...prev,
        paymentError: 'No unpaid orders in the current selection.',
      }))
      return
    }

    for (const product of unpaidOrders) {
      const order = normalizeProductForPayment(product)
      const orderDetails = getOrderPaymentDetailsFromOrder(order)
      const orderRemaining = Number(orderDetails.remainingAmount ?? 0)
      if (remainingAmount > 0 && amount > orderRemaining) {
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          paymentError: `${order.order_id}: Installment amount cannot exceed the remaining amount`,
        }))
        return
      }
    }

    const installmentAmount = remainingAmount === 0 ? 0 : Number(bulkPaymentStatusModal.installmentAmount)
    const creditNote = bulkPaymentStatusModal.creditNote || ''

    setBulkPaymentStatusModal((prev) => ({ ...prev, isUpdating: true, error: '', paymentError: '' }))

    try {
      const responses = await Promise.all(
        unpaidOrders.map((product) => {
          const order = normalizeProductForPayment(product)
          const orderDetails = getOrderPaymentDetailsFromOrder(order)
          const orderRemaining = Number(orderDetails.remainingAmount ?? 0)
          const orderInstallment = orderRemaining === 0 ? 0 : installmentAmount
          const orderModalState = {
            ...bulkCreditState,
            order,
            orderPaymentDetails: orderDetails,
          }
          const formData = buildNewCreditPaymentFormData(
            orderModalState,
            order.order_id,
            orderInstallment,
            creditNote
          )
          return apiService.putMultipart(API_ENDPOINTS.UPDATE_new_payment_status, formData)
        })
      )
      const allSuccess = responses.every((response) => response?.success)
      if (allSuccess) {
        toast.showSuccess(responses[0]?.message || 'Payment status updated successfully')
        setBulkPaymentStatusModal({ ...DEFAULT_BULK_PAYMENT_STATUS_MODAL })
        await onRefresh?.()
      } else {
        const failed = responses.find((response) => !response?.success)
        const message = failed?.message || 'Some payment status updates failed. Please try again.'
        toast.showError(message)
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          isUpdating: false,
          error: message,
        }))
      }
    } catch (error) {
      console.error('Error submitting bulk credit payment:', error)
      const message = extractPaymentApiError(error)
      toast.showError(message)
      setBulkPaymentStatusModal((prev) => ({
        ...prev,
        isUpdating: false,
        error: message,
      }))
    }
  }

  const handleConfirmBulkPaidPaymentDetails = async () => {
    const bulkPaidState = {
      ...bulkPaymentStatusModal,
      order: bulkPaymentStatusModal.summaryOrder,
    }
    const unpaidOrders = getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment())
    if (unpaidOrders.length === 0) {
      setBulkPaymentStatusModal((prev) => ({
        ...prev,
        paymentError: 'No unpaid orders in the current selection.',
      }))
      return
    }

    for (const product of unpaidOrders) {
      const order = normalizeProductForPayment(product)
      const orderDetails = getOrderPaymentDetailsFromOrder(order)
      const orderModalState = {
        ...bulkPaidState,
        order,
        orderPaymentDetails: orderDetails,
        advanceAmount: String(orderDetails.remainingAmount),
        paymentMethodConfig:
          paymentMethodsById[bulkPaidState.paymentMethod] || bulkPaidState.paymentMethodConfig,
      }
      const validationError = validatePaidPaymentDetails(orderModalState)
      if (validationError) {
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          paymentError: `${order.order_id}: ${validationError}`,
        }))
        return
      }
    }

    setBulkPaymentStatusModal((prev) => ({ ...prev, isUpdating: true, error: '', paymentError: '' }))

    try {
      const responses = await Promise.all(
        unpaidOrders.map((product) => {
          const order = normalizeProductForPayment(product)
          const orderDetails = getOrderPaymentDetailsFromOrder(order)
          const orderModalState = {
            ...bulkPaidState,
            order,
            orderPaymentDetails: orderDetails,
            advanceAmount: String(orderDetails.remainingAmount),
          }
          return submitNewPaidPaymentStatus(orderModalState, order.order_id)
        })
      )
      const allSuccess = responses.every((response) => response?.success)
      if (allSuccess) {
        toast.showSuccess(responses[0]?.message || 'Payment status updated successfully')
        setBulkPaymentStatusModal({ ...DEFAULT_BULK_PAYMENT_STATUS_MODAL })
        await onRefresh?.()
      } else {
        const failed = responses.find((response) => !response?.success)
        const message = failed?.message || 'Some payment status updates failed. Please try again.'
        toast.showError(message)
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          isUpdating: false,
          error: message,
        }))
      }
    } catch (error) {
      console.error('Error updating bulk paid payment:', error)
      const message = extractPaymentApiError(error)
      toast.showError(message)
      setBulkPaymentStatusModal((prev) => ({
        ...prev,
        isUpdating: false,
        error: message,
      }))
    }
  }

  // Handle bulk payment status update from separate modal - update all selected orders
  const handleBulkPaymentStatusUpdateFromModal = async () => {
    if (!bulkPaymentStatusModal.selectedPaymentStatus) {
      setBulkPaymentStatusModal(prev => ({
        ...prev,
        error: 'Please select a payment status'
      }))
      return
    }

    if (bulkPaymentStatusModal.selectedPaymentStatus === 'paid') {
      handleConfirmBulkPaidPaymentDetails()
      return
    }

    setBulkPaymentStatusModal(prev => ({ ...prev, isUpdating: true, error: '' }))
    try {
      const userId = localStorage.getItem("user_id");
      const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
      const selectedOrderIds = selectedOrders.map(order => order.order_id).filter(Boolean);

      if (selectedOrderIds.length === 0) {
        setBulkPaymentStatusModal(prev => ({
          ...prev,
          error: 'No orders selected',
          isUpdating: false
        }))
        return
      }

      // Update payment status for all selected orders
      const updatePromises = selectedOrderIds.map(orderId => {
        const payload = {
          order_id: orderId,
          payment_status: bulkPaymentStatusModal.selectedPaymentStatus,
          user_id: userId,
        };
        return apiService.put(
          `${API_ENDPOINTS.UPDATE_payment_status}`,
          payload
        );
      });

      const responses = await Promise.all(updatePromises);
      const allSuccess = responses.every(response => response?.success);

      if (allSuccess) {
        toast.showSuccess('Payment status updated successfully')
        setBulkPaymentStatusModal({
          open: false,
          selectedPaymentStatus: '',
          error: '',
          isUpdating: false,
          summaryOrder: null,
        })
        setPaymentStatusChoices([])
        // Clear all selections
        if (onSelectAll) {
          onSelectAll(false)
        }
        setTimeout(() => {
          onRefresh?.()
        }, 100)
        // Toast removed as per user request
      } else {
        setBulkPaymentStatusModal(prev => ({
          ...prev,
          error: 'Some payment status updates failed. Please try again.',
          isUpdating: false
        }))
      }
    } catch (error) {
      console.error("Error updating bulk payment status:", error)
      const message = extractPaymentApiError(error)
      toast.showError(message)
      setBulkPaymentStatusModal(prev => ({
        ...prev,
        error: message,
        isUpdating: false
      }))
    }
  }

  // Handle bulk payment status update from bulk challan modal - only update unpaid orders
  const handleBulkPaymentStatusUpdate = async () => {
    if (!bulkChallanModal.bulkPaymentStatus) {
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'Please select a payment status'
      }))
      return
    }

    setIsUpdatingBulkPayment(true)
    try {
      const userId = localStorage.getItem("user_id");
      const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
      
      // Filter to only get unpaid orders (not paid or outstanding)
      const unpaidOrders = selectedOrders.filter(order => {
        const paymentStatus = (order.payment_status?.name || order.payment_status_name || order.payment_status || '').toLowerCase();
        const isPaid = paymentStatus === 'paid' || paymentStatus === 'outstanding';
        return !isPaid;
      });

      if (unpaidOrders.length === 0) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'All selected orders are already paid or outstanding. No payment status update needed.'
        }))
        setIsUpdatingBulkPayment(false)
        return
      }

      const unpaidOrderIds = unpaidOrders.map(order => order.order_id).filter(Boolean);

      // Update payment status only for unpaid orders
      const updatePromises = unpaidOrderIds.map(orderId => {
        const payload = {
          order_id: orderId,
          payment_status: bulkChallanModal.bulkPaymentStatus,
          user_id: userId,
        };
        return apiService.put(
          `${API_ENDPOINTS.UPDATE_payment_status}`,
          payload
        );
      });

      const responses = await Promise.all(updatePromises);
      const allSuccess = responses.every(response => response?.success);

      if (allSuccess) {
        // Clear the payment status selection and refresh data
        setBulkChallanModal(prev => ({
          ...prev,
          bulkPaymentStatus: '',
          error: ''
        }))
        await onRefresh()
      } else {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'Some payment status updates failed. Please try again.'
        }))
      }
    } catch (error) {
      console.error("Error updating bulk payment status:", error)
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'Error updating payment status. Please try again.'
      }))
    } finally {
      setIsUpdatingBulkPayment(false)
    }
  }

  // Open bulk payment status update modal
  const handleOpenBulkPaymentStatusModal = () => {
    if (isBulkPaymentUpdateDisabled()) return
    const selected = getSelectedOrdersForBulkPayment()
    const summaryOrder = normalizeProductForPayment(selected[0] || null)
    setBulkPaymentStatusModal({
      ...DEFAULT_BULK_PAYMENT_STATUS_MODAL,
      open: true,
      summaryOrder,
    })
    fetchPaymentStatusChoicesForOrder(summaryOrder?.order_id)
    loadAdvancePaymentMethods()
    closeActionsMenu()
  }

  const handleCloseBulkPaymentStatusModal = () => {
    if (bulkPaymentStatusModal.isUpdating) return
    setBulkPaymentStatusModal({ ...DEFAULT_BULK_PAYMENT_STATUS_MODAL })
    setPaymentStatusChoices([])
  }
  
  // Handle individual product selection
  const handleProductSelection = (productId, isSelected) => {
    if (onProductSelect) {
      onProductSelect(productId, isSelected)
    }
  }

  // Handle select all toggle
  const handleSelectAllToggle = () => {
    if (onSelectAll) {
      onSelectAll(!isAllSelected)
    }
  }

  // Check if a product is selected
  const isProductSelected = (productId) => {
    return selectedProducts.includes(productId)
  }

  // Handle invoice view
  const handleViewInvoice = (product) => {
    if (product.invoice && product.invoice.path) {
      setInvoiceModal({
        open: true,
        invoice: product.invoice,
        product: product
      })
    }
  }

  // Handle invoice download
  const handleDownloadInvoice = async (invoice, productName) => {
    if (!invoice || !invoice.path) {
      alert('Invoice not available for download')
      return
    }

    setDownloadingInvoice(true)
    try {
      // Create a temporary link to download the PDF
      const link = document.createElement('a')
      link.href = invoice.path
      link.download = `Invoice_${invoice.id}_${productName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Error downloading invoice:', error)
      alert('Error downloading invoice. Please try again.')
    } finally {
      setDownloadingInvoice(false)
    }
  }

  // Close invoice modal
  const handleCloseInvoiceModal = () => {
    setInvoiceModal({ open: false, invoice: null, product: null })
  }

  // Handle delivery challan view
  const handleViewDeliveryChallan = (product) => {
    if (product.delivery_challan && product.delivery_challan.path) {
      setDeliveryChallanModal({
        open: true,
        challan: product.delivery_challan,
        product: product
      })
    }
  }

  // Handle delivery challan download
  const handleDownloadDeliveryChallan = async (challan, productName) => {
    if (!challan || !challan.path) {
      alert('Delivery challan not available for download')
      return
    }

    setDownloadingChallan(true)
    try {
      // Create a temporary link to download the PDF
      const link = document.createElement('a')
      link.href = challan.path
      link.download = `Delivery_Challan_${challan.id}_${productName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Error downloading delivery challan:', error)
      alert('Error downloading delivery challan. Please try again.')
    } finally {
      setDownloadingChallan(false)
    }
  }

  // Close delivery challan modal
  const handleCloseDeliveryChallanModal = () => {
    setDeliveryChallanModal({ open: false, challan: null, product: null })
  }

  // Handle status modal opening
  const handleOpenStatusModal = (product) => {
    // Store the original status before any changes
    const originalStatus = {
      name: product?.status_name || product?.status?.name || '',
      category: product?.status_category || product?.status?.category || ''
    };
    
    // Get the next sequential status
    const nextStatus = getNextSequentialStatus(originalStatus.name, originalStatus.category);
    
    // Create updated order object with next status pre-selected
    const updatedOrder = nextStatus ? {
      ...product,
      status_name: nextStatus.name,
      status_category: nextStatus.category,
      status: {
        name: nextStatus.name,
        category: nextStatus.category
      }
    } : product;

    const truckOrderWithStartKm = getTruckOrderWithStartKm([product]);
    const truckChallan = truckOrderWithStartKm?.delivery_challan;
    
    setStatusUpdateModal({
      open: true,
      order: updatedOrder, // Order with next status pre-selected
      originalStatus: originalStatus, // Store original status for validation
      entityName: '',
      orderBy: '',
      driverName: truckChallan?.driver_name || '',
      vehicleNo:
        truckChallan?.vehicle_number ||
        truckChallan?.vehicle_no ||
        truckChallan?.vehicle_name ||
        '',
      shippedDate: new Date().toISOString().slice(0,10),
      generateInvoice: false,
      isCreatingChallan: false,
      challanError: '',
      emails: [],
      emailInput: '',
      dispatchPersonName: '',
      customDispatchPerson: '',
      useCustomDispatchPerson: false,
      start_km:
        truckChallan?.start_km !== null && truckChallan?.start_km !== undefined
          ? String(truckChallan.start_km)
          : '',
      end_km: '' 
    });
  };

  // Handle status modal closing
  const handleCloseStatusModal = () => {
    setStatusUpdateModal({
      open: false,
      order: null,
      originalStatus: null,
      entityName: '',
      orderBy: '',
      driverName: '',
      vehicleNo: '',
      shippedDate: new Date().toISOString().slice(0,10),
      generateInvoice: false,
      isCreatingChallan: false,
      challanError: '',
      emails: [],
      emailInput: '',
      dispatchPersonName: '',
      customDispatchPerson: '',
      useCustomDispatchPerson: false,
      start_km: '',
      end_km: '',
      cancellationReason: '',
      returnReason: ''
    });
  };

  // Handle input changes in status modal
  const handleStatusModalChange = (field, value) => {
    setStatusUpdateModal(prev => ({
      ...prev,
      [field]: value,
      challanError: '' // Clear error when user types
    }));
  };

  // Handle bulk delivery challan creation
  const handleBulkDeliveryChallan = () => {
    // Find minimum status among selected orders to determine next sequential status
    const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
    const statusOrder = getStatusOrder();
    
    // Get the minimum status order (lowest sequence) among selected orders
    let minStatusOrder = 999;
    let minStatusCategory = '';
    
    selectedOrders.forEach(order => {
      const orderStatusCategory = (order.status_category || '').toLowerCase();
      const orderStatusName = order.status_name || '';
      
      // Try to find category from statusOptions if not directly available
      if (!orderStatusCategory && orderStatusName) {
        const orderStatusOption = statusOptions.find(opt => opt.name === orderStatusName);
        orderStatusCategory = orderStatusOption?.category?.toLowerCase() || '';
      }
      
      const orderSequence = statusOrder[orderStatusCategory] || 0;
      if (orderSequence > 0 && orderSequence < minStatusOrder) {
        minStatusOrder = orderSequence;
        minStatusCategory = orderStatusCategory;
      }
    });
    
    let defaultStatus = null;
    
    if (statusFilter === 'Dispatch') {
      // When Dispatch tab is active, default to Delivered (next sequential status)
      defaultStatus = statusOptions.find(status => status.name === 'Delivered') || 
                      statusOptions.find(status => status.name === 'Returned') ||
                      statusOptions.find(status => status.name === 'Cancelled') ||
                      (statusOptions.length > 0 ? statusOptions[0] : null);
    } else if (statusFilter === 'Delivered') {
      // Keep bulk behavior aligned with single status update: Delivered -> Returned only.
      defaultStatus = statusOptions.find(status => status.name === 'Returned') || null;
    } else if (minStatusOrder !== 999 && minStatusOrder > 0) {
      // Find the next sequential status based on minimum current status
      const nextSequence = minStatusOrder + 1;
      defaultStatus = statusOptions.find(status => {
        const statusCategory = (status.category || '').toLowerCase();
        return statusOrder[statusCategory] === nextSequence;
      });
      
      // If next sequential not found, try to find any available next status
      if (!defaultStatus) {
        defaultStatus = statusOptions.find(status => {
          const statusCategory = (status.category || '').toLowerCase();
          const statusSeq = statusOrder[statusCategory] || 0;
          return statusSeq > minStatusOrder && statusSeq <= minStatusOrder + 1;
        });
      }
      
      // Fallback to first available status if no next sequential found
      if (!defaultStatus) {
        defaultStatus = statusOptions.length > 0 ? statusOptions[0] : null;
      }
    } else {
      // If no valid status found, default to first status (safety fallback)
      defaultStatus = statusOptions.length > 0 ? statusOptions[0] : null;
    }
    
    const truckOrderWithStartKm = getTruckOrderWithStartKm(selectedOrders);
    const truckChallan = truckOrderWithStartKm?.delivery_challan;

    setBulkChallanModal({
      open: true,
      driverName: truckChallan?.driver_name || '',
      vehicleNo:
        truckChallan?.vehicle_number ||
        truckChallan?.vehicle_no ||
        truckChallan?.vehicle_name ||
        '',
      deliveryDate: new Date().toISOString().slice(0,10),
      generateInvoice: false,
      isCreating: false,
      error: '',
      emails: [],
      emailInput: '',
      selectedStatus: defaultStatus?.name || '',
      selectedStatusCategory: defaultStatus?.category || '',
      dispatchPersonName: '',
      customDispatchPerson: '',
      useCustomDispatchPerson: false,
      start_km:
        truckChallan?.start_km !== null && truckChallan?.start_km !== undefined
          ? String(truckChallan.start_km)
          : '',
      end_km: '',
      cancellationReason: '',
      returnReason: ''
    })
  }

  // Create bulk delivery challan
  const createBulkDeliveryChallan = async (orderIds, driverName, vehicleNo) => {
    try {
      const formData = new FormData()
      formData.append('order_id', orderIds.join(',')) // Comma-separated order IDs
      formData.append('driver_name', driverName)
      formData.append('vehicle_no', vehicleNo)
      if (bulkChallanModal.deliveryDate) {
        formData.append('delivery_date', bulkChallanModal.deliveryDate)
      }
      formData.append('invoice', bulkChallanModal.generateInvoice ? 'true' : 'false')
      if (bulkChallanModal.emails?.length > 0) {
        formData.append('emails', bulkChallanModal.emails.join(','))
      }
      if (bulkChallanModal.start_km) {
        formData.append('start_km', bulkChallanModal.start_km)
      }
      
      // Add dispatch person name
      const dispatchPersonName = bulkChallanModal.useCustomDispatchPerson 
        ? bulkChallanModal.customDispatchPerson 
        : bulkChallanModal.dispatchPersonName;
      if (dispatchPersonName) {
        formData.append('dispatch_person_name', dispatchPersonName);
      }
      // Add status to bulk delivery challan creation
      if (bulkChallanModal.selectedStatus) {
        formData.append('status', bulkChallanModal.selectedStatus)
      }

      const response = await apiService.post(
        API_ENDPOINTS.POST_create_delivery_challan,
        formData,
        "multipart/form-data"
      )

      return response
    } catch (error) {
      console.error('Error creating bulk delivery challan:', error)
      throw error
    }
  }

  // Handle bulk challan submission
  const handleBulkChallanSubmit = async () => {
    const { driverName, vehicleNo, selectedStatus } = bulkChallanModal

    // Validation
    if (!selectedStatus.trim()) {
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'Please select a status to update orders to'
      }))
      return
    }

    if (selectedProducts.length === 0) {
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'No products selected'
      }))
      return
    }

    // Validate sequential status update
    const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
    const statusOrder = getStatusOrder();
    const selectedOption = statusOptions.find(opt => opt.name === selectedStatus);
    const newStatusCategory = (selectedOption?.category || '').toLowerCase();
    const newStatusName = (selectedStatus || '').toLowerCase();
    
    const processingOrder = statusOrder['processing'] || 0;
    const deliveredOrder = statusOrder['delivered'] || 999;
    const dispatchOrder = statusOrder['shipped'] || statusOrder['dispatch'] || 999;
    
    const isReturned = newStatusCategory === 'returned' || newStatusName === 'returned';
    const isCancelledOrReturned = newStatusCategory === 'cancelled' || newStatusCategory === 'returned' ||
                                   newStatusName === 'cancelled' || newStatusName === 'returned';
    const isDispatch = newStatusCategory === 'shipped' || newStatusCategory === 'dispatch' || newStatusName === 'dispatch';
    
    // Find minimum status among selected orders
    let minStatusOrder = 999;
    let minStatusCategory = '';
    let minStatusName = '';
    let hasDelivered = false;
    let hasCancelled = false;
    
    selectedOrders.forEach(order => {
      const orderStatusCategory = (order.status_category || order.status?.category || '').toLowerCase();
      const orderStatusName = (order.status_name || order.status?.name || '').toLowerCase();
      
      // Check if any order is Delivered
      if (orderStatusName === 'delivered' || orderStatusCategory === 'delivered' || 
          statusOrder[orderStatusCategory] === deliveredOrder) {
        hasDelivered = true;
      }
      
      // Check if any order is Cancelled
      if (orderStatusName === 'cancelled' || orderStatusCategory === 'cancelled') {
        hasCancelled = true;
      }
      
      // Find order sequence
      let orderCategory = orderStatusCategory;
      if (!orderCategory && orderStatusName) {
        const orderStatusOption = statusOptions.find(opt => opt.name === orderStatusName);
        orderCategory = (orderStatusOption?.category || '').toLowerCase();
      }
      
      const orderSequence = statusOrder[orderCategory] || 0;
      if (orderSequence > 0 && orderSequence < minStatusOrder) {
        minStatusOrder = orderSequence;
        minStatusCategory = orderCategory;
        minStatusName = orderStatusName;
      }
    });
    
    // Special case: If any order is Cancelled, no further status changes are allowed
    if (hasCancelled) {
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'Cancelled is a final status. No further status changes are allowed for cancelled orders.'
      }));
      return;
    }
    
    // Special case: If any order is Delivered, only allow Returned
    if (hasDelivered && !isReturned) {
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'When an order is Delivered, only Returned status is allowed.'
      }));
      return;
    }
    
    // Check if current minimum status is Processing for Dispatch
    const isProcessingStatus = minStatusName === 'processing' || 
                               minStatusCategory === 'processing' ||
                               minStatusOrder === processingOrder;
    
    // Dispatch is only available when current status is Processing
    if (isDispatch && !isProcessingStatus) {
      setBulkChallanModal(prev => ({
        ...prev,
        error: `Dispatch status is only available after orders have been successfully updated to Processing. Current minimum status: ${minStatusName || minStatusCategory || 'Unknown'}.`
      }));
      return;
    }
    
    // Returned is only available from Delivered status
    if (isReturned && !hasDelivered) {
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'Returned status is only available when orders are Delivered.'
      }));
      return;
    }
    
    // For non-cancelled/returned statuses, enforce sequential flow
    if (!isCancelledOrReturned) {
      if (minStatusOrder === 999) {
        minStatusOrder = 0;
      }
      
      const newStatusOrder = statusOrder[newStatusCategory] || 0;
      
      // Check if trying to skip sequential steps
      if (newStatusOrder > minStatusOrder + 1) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: `Status must be updated sequentially. You can only move to the next status in the sequence. Minimum current status: ${minStatusName || minStatusCategory || 'Unknown'}, Next allowed: ${statusOptions.find(opt => {
            const optOrder = statusOrder[(opt.category || '').toLowerCase()] || 0;
            return optOrder === minStatusOrder + 1;
          })?.name || 'N/A'}.`
        }))
        return
      }
      
      // Check if trying to go backwards
      if (newStatusOrder < minStatusOrder) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: `Cannot move to a previous status. Minimum current status: ${minStatusName || minStatusCategory || 'Unknown'}.`
        }))
        return
      }
    }

    // Only validate delivery fields for Dispatch status
    if (selectedStatus === 'Dispatch') {
      const isTruckOrderBy = String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck';
      if (!driverName.trim() || !bulkChallanModal.deliveryDate || (isTruckOrderBy && !vehicleNo.trim())) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'Driver name, vehicle number, and Delivery Date are required for Dispatch status'
        }))
        return
      }

      if (isTruckOrderBy && isVehicleReferenceNoInvalid(vehicleNo)) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'Vehicle No / Reference No must be in format MH 12 AB 1234'
        }))
        return
      }
      
      // Validate dispatch person (either selected or custom)
      const dispatchPersonName = bulkChallanModal.useCustomDispatchPerson 
        ? bulkChallanModal.customDispatchPerson 
        : bulkChallanModal.dispatchPersonName;
      if (!dispatchPersonName?.trim()) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'Dispatch Person Name is required. Please select a dispatch person or add a custom one.'
        }))
        return
      }
    }

    setBulkChallanModal(prev => ({
      ...prev,
      isCreating: true,
      error: ''
    }))

    try {
      // Get order IDs from selected products
      const selectedOrderIds = filteredData
        .filter(product => selectedProducts.includes(product.id))
        .map(product => product.order_id)
        .filter(Boolean) // Remove any null/undefined order IDs

      if (selectedOrderIds.length === 0) {
        throw new Error('No valid orders found for selected products')
      }

      let response = { success: true }; // Default success for non-dispatch statuses
      
      // Only create delivery challans for Dispatch status
      if (selectedStatus === 'Dispatch') {
        console.log('Creating bulk delivery challan for orders:', selectedOrderIds)
        response = await createBulkDeliveryChallan(selectedOrderIds, driverName, vehicleNo)
      }

      if (response?.success) {
        // Update order statuses - BULK API CALL with comma-separated order IDs
        console.log('Updating order statuses to:', selectedStatus);
        console.log('Order IDs to update:', selectedOrderIds);
        
          try {
          const userId = localStorage.getItem("user_id");
          const dispatchPersonName = bulkChallanModal.useCustomDispatchPerson 
            ? bulkChallanModal.customDispatchPerson 
            : bulkChallanModal.dispatchPersonName;
          const payload = {
            user_id: userId,
            status: selectedStatus,
            order_id: selectedOrderIds.join(','), // Comma-separated order IDs
            emails: bulkChallanModal.emails?.length > 0 ? bulkChallanModal.emails.join(',') : '',
            generateInvoice: bulkChallanModal.generateInvoice,
            driverName: selectedStatus === 'Dispatch' ? bulkChallanModal.driverName : '',
            vehicleNo: selectedStatus === 'Dispatch' ? bulkChallanModal.vehicleNo : '',
            delivery_date: bulkChallanModal.deliveryDate || new Date().toISOString().slice(0,10),
            dispatch_person_name: dispatchPersonName,
            start_km: bulkChallanModal.start_km || '',  // new
            ...(selectedStatus === 'Delivered' && { end_km: bulkChallanModal.end_km || '' }),
            ...(selectedStatus === 'Cancelled' && { reason: bulkChallanModal.cancellationReason || '' }),
            ...(selectedStatus === 'Returned' && { reason: bulkChallanModal.returnReason || '' })
          };

          console.log('Bulk status update payload:', payload);
            
          const statusResponse = await apiService.put(
              API_ENDPOINTS.UPDATE_product_status,
            payload,
            "application/json"
            );
            
          console.log('Bulk status update response:', statusResponse);

          if (statusResponse?.success) {
        setBulkChallanModal({
          open: false,
          driverName: '',
          vehicleNo: '',
          deliveryDate: new Date().toISOString().slice(0,10),
          generateInvoice: false,
          isCreating: false,
          error: '',
          emails: [],
          emailInput: '',
          selectedStatus: '',
          selectedStatusCategory: '',
          dispatchPersonName: '',
          customDispatchPerson: '',
          useCustomDispatchPerson: false,
          start_km: '',
          end_km: '',
          cancellationReason: '',
          returnReason: '',
          bulkPaymentStatus: ''
        })
        
        // Clear selections and refresh data
        if (onSelectAll) {
          onSelectAll(false)
        }
        setTimeout(() => {
          onRefresh?.()
        }, 100)
        
        // Toast removed as per user request
          } else {
            throw new Error(statusResponse?.message || 'Failed to update order statuses')
          }
        } catch (statusError) {
          console.error('Error updating order statuses:', statusError);
          throw new Error(statusError?.message || 'Failed to update order statuses');
        }
      } else {
        throw new Error(response?.message || 'Failed to create bulk delivery challan')
      }
    } catch (error) {
      console.error('Error in bulk operation:', error)
      setBulkChallanModal(prev => ({
        ...prev,
        error: error.message || 'Failed to process bulk operation',
        isCreating: false
      }))
    }
  }

  // Close bulk challan modal
  const handleCloseBulkChallanModal = () => {
    setBulkChallanModal({
      open: false,
       orderBy: '',
      entityName: '',
      driverName: '',
      vehicleNo: '',
      deliveryDate: new Date().toISOString().slice(0,10),
      generateInvoice: false,
      isCreating: false,
      error: '',
      emails: [],
      emailInput: '',
      selectedStatus: '',
      selectedStatusCategory: '',
      dispatchPersonName: '',
      customDispatchPerson: '',
      useCustomDispatchPerson: false,
      start_km: '',
      end_km: '',
      cancellationReason: '',
      returnReason: '',
      bulkPaymentStatus: ''
    })
  }

  // Handle bulk challan input changes
  const handleBulkChallanInputChange = (field, value) => {
    setBulkChallanModal(prev => ({
      ...prev,
      [field]: value,
      error: '' // Clear error when user types
    }))

    // When orderBy changes, fetch entities and clear entityName
    if (field === 'orderBy') {
      setBulkChallanModal(prev => ({
        ...prev,
        orderBy: value,
        entityName: '', // Clear entity name when order by changes
        error: ''
      }));
      fetchBulkEntitiesByOrderBy(value);
    }
  }

  // Fetch entities for bulk orders based on order_by selection
  const fetchBulkEntitiesByOrderBy = async (orderBy) => {
    if (!orderBy) {
      setBulkEntityOptions([]);
      return;
    }
    
    setLoadingBulkEntities(true);
    try {
      // TODO: Replace with actual API endpoint when available
      // For now, using a mapping based on order_by value
      // Example: const response = await apiService.get(`${API_ENDPOINTS.GET_entities_by_order_by}?order_by=${orderBy}`);
      
      // Mock data - replace with actual API call
      const entityMapping = {
        'indian post': ['Mumbai Branch', 'Delhi Branch', 'Bangalore Branch', 'Chennai Branch'],
        'courier services': ['BlueDart', 'FedEx', 'DTDC', 'First Flight', 'Professional Courier'],
        'name': ['Mumbai Branch', 'Delhi Branch', 'Bangalore Branch', 'Chennai Branch']
      };
      
      const entities = entityMapping[orderBy.toLowerCase()] || [];
      setBulkEntityOptions(entities);
    } catch (error) {
      console.error('Error fetching bulk entities:', error);
      setBulkEntityOptions([]);
    } finally {
      setLoadingBulkEntities(false);
    }
  } 

  // Add email to bulk challan modal
  const addEmailToBulkModal = () => {
    const email = bulkChallanModal.emailInput?.trim() || ''
    if (email && !bulkChallanModal.emails?.includes(email)) {
      setBulkChallanModal(prev => ({
        ...prev,
        emails: [...(prev.emails || []), email],
        emailInput: ''
      }))
    }
  }

  // Remove email from bulk challan modal
  const removeEmailFromBulkModal = (emailToRemove) => {
    setBulkChallanModal(prev => ({
      ...prev,
      emails: (prev.emails || []).filter(email => email !== emailToRemove)
    }))
  }

  // Add email to status update modal
  const addEmailToStatusModal = () => {
    const email = statusUpdateModal.emailInput?.trim() || ''
    if (email && !statusUpdateModal.emails?.includes(email)) {
      setStatusUpdateModal(prev => ({
        ...prev,
        emails: [...(prev.emails || []), email],
        emailInput: ''
      }))
    }
  }

  // Remove email from status update modal
  const removeEmailFromStatusModal = (emailToRemove) => {
    setStatusUpdateModal(prev => ({
      ...prev,
      emails: (prev.emails || []).filter(email => email !== emailToRemove)
    }))
  }

  // Get eligible products for bulk challan (those without existing delivery challan)
  const getEligibleProductsCount = () => {
    return filteredData
      .filter(product => selectedProducts.includes(product.id))
      .filter(product => !product.delivery_challan || !product.delivery_challan.path)
      .length
  }

  const getSelectedProductsCount = () => {
    return filteredData
      .filter(product => selectedProducts.includes(product.id))
      .length
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  if (bulkProducts.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6" color="textSecondary">
          No bulk products found
        </Typography>
        <Button 
          variant="outlined" 
          color="primary" 
          onClick={onRefresh}
          startIcon={<UpdateIcon />}
          sx={{ mt: 2 }}
        >
          Refresh
        </Button>
      </Box>
    )
  }

  // Product Details Row Component (similar to OrderDetailsRow in ProductList)
  const ProductDetailsRow = ({ product }) => (
    <ExpandableRow>
      <StyledTableCell colSpan={8} sx={{ paddingY: 0, paddingX: 0 }}>
        <Collapse in={expandedRows.has(product.id)} timeout="auto" unmountOnExit>
          <Box sx={{ width: '100%', padding: 2 }}>
            <Grid container spacing={2.5} alignItems="stretch">
              <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                <DetailCard 
                  elevation={2} 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: 'background.paper', 
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, 
                    borderRadius: 3,
                    p: 2.5
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', mb: 2 }}>
                    <Person sx={{ mr: 1, color: theme.palette.primary.main, flexShrink: 0 }} />
                    <Typography variant="h6" component="span" fontWeight={600} sx={{ mb: 0 }}>Customer Information</Typography>
                  </Box>
                  
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Name:</strong> {product.customer_name}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Order ID:</strong> {product.order_id || "N/A"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Email:</strong> {product.email}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Order Date:</strong> {product.order_date_formatted || product.order?.order_date_formatted || "N/A"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Mobile:</strong> {product.mobile_number}
                  </Typography>

                  {(product.order?.farmer_name || getAdvancePaymentEntries(product)[0]?.order?.farmer_name || product.advance_payment?.order?.farmer_name) && (
                    <Box sx={{ mt: 1, mb: 1, p: 1.5, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 1, border: `1px solid ${alpha(theme.palette.info.main, 0.3)}` }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "grey.600", mb: 0.5 }}>
                        Order on Behalf of : {product.order?.farmer_name || getAdvancePaymentEntries(product)[0]?.order?.farmer_name || product.advance_payment?.order?.farmer_name}
                      </Typography>
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
                    <LocationOn sx={{ mr: 1, color: theme.palette.secondary.main }} />
                    <Typography variant="subtitle2" fontWeight={600}>Delivery Address</Typography>
                  </Box>
                  <Tooltip
                    title={
                      product.advance_payment?.order?.delivery_address
                      || getAdvancePaymentEntries(product)[0]?.order?.delivery_address
                      || (product.address
                        ? [product.address.village, product.address.tehsil, product.address.district, product.address.state, product.address.pincode].filter(Boolean).join(', ')
                        : 'Address not available')
                    }
                    arrow
                    placement="top"
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ backgroundColor: alpha(theme.palette.background.default, 0.5), padding: 1.5, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
                      {product.advance_payment?.order?.delivery_address
                        || getAdvancePaymentEntries(product)[0]?.order?.delivery_address
                        || (product.address
                          ? [product.address.village, product.address.tehsil, product.address.district, product.address.state, product.address.pincode].filter(Boolean).join(', ')
                          : 'Address not available')}
                    </Typography>
                  </Tooltip>

                  {product.order?.notes && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }} fontWeight={600}>Special Instructions:</Typography>
                      <Tooltip title={product.order.notes} arrow placement="top">
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', p: 1.5, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 1, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
                          {product.order.notes}
                        </Typography>
                      </Tooltip>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
                    <LocationOn sx={{ mr: 1, color: theme.palette.warning.main }} />
                    <Typography variant="subtitle2" fontWeight={600}>Billing Address</Typography>
                  </Box>
                  <Tooltip
                    title={
                      product.advance_payment?.order?.billing_address
                      || getAdvancePaymentEntries(product)[0]?.order?.billing_address
                      || product.advance_payment?.order?.delivery_address
                      || getAdvancePaymentEntries(product)[0]?.order?.delivery_address
                      || (product.address
                        ? [product.address.village, product.address.tehsil, product.address.district, product.address.state, product.address.pincode].filter(Boolean).join(', ')
                        : 'Billing address not available')
                    }
                    arrow
                    placement="top"
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ backgroundColor: alpha(theme.palette.background.default, 0.5), padding: 1.5, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
                      {product.advance_payment?.order?.billing_address
                        || getAdvancePaymentEntries(product)[0]?.order?.billing_address
                        || product.advance_payment?.order?.delivery_address
                        || getAdvancePaymentEntries(product)[0]?.order?.delivery_address
                        || (product.address
                          ? [product.address.village, product.address.tehsil, product.address.district, product.address.state, product.address.pincode].filter(Boolean).join(', ')
                          : 'Billing address not available')}
                    </Typography>
                  </Tooltip>
                </DetailCard>
              </Grid>
              <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                <DetailCard elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2.5, backgroundColor: 'background.paper', border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', mb: 2.5 }}>
                    <LocationOn sx={{ mr: 1.25, fontSize: '1.25rem', color: theme.palette.success.main, flexShrink: 0 }} />
                    <Typography variant="h6" component="span" fontWeight={600} sx={{ mb: 0 }}>Source Information</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1.25 }}>
                    <strong>Source:</strong> {product.source}
                  </Typography>
                  {(product.expected_delivery_date || product.order?.expected_delivery_date) && (
                    <Typography variant="body2" sx={{ mb: 1.25 }}>
                      <strong>Expected Delivery Date (Farmer):</strong>{' '}
                      <Typography component="span" variant="body2" color="primary" fontWeight={600}>
                        {new Date(product.expected_delivery_date || product.order?.expected_delivery_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </Typography>
                    </Typography>
                  )}
                  {(product.delivery_date || product.order?.delivery_date) && ['delivered', 'returned'].includes(
                    (product.status_category || product.status_name || product.status?.category || product.status?.name || product.status || '').toString().toLowerCase()
                  ) && (
                    <Typography variant="body2" sx={{ mb: 1.25 }}>
                      <strong>Actual Delivery Date:</strong>{' '}
                      <Typography component="span" variant="body2" color="success.main" fontWeight={600}>
                        {(() => {
                          const deliveryDate = product.delivery_date || product.order?.delivery_date;
                          try {
                            return new Date(deliveryDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            });
                          } catch (e) {
                            return product.order?.delivery_date_formatted || deliveryDate;
                          }
                        })()}
                      </Typography>
                    </Typography>
                  )}
                  {product.gst_no && (
                    <Typography variant="body2" sx={{ mb: 1.25 }}>
                      <strong>GST No:</strong> {product.gst_no}
                    </Typography>
                  )}
                  {/* <Typography variant="body2" sx={{ mb: 1.25 }}>
                    <strong>Order Date:</strong> {product.order_date_formatted || product.order?.order_date_formatted || new Date(product.created_at).toLocaleString()}
                  </Typography> */}
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Payment sx={{ mr: 1.25, fontSize: '1.25rem', color: theme.palette.info.main }} />
                    <Typography variant="subtitle1" fontWeight={600}>Payment Information</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1.25 }}>
                    <strong>Payment Status:</strong>
                    <PaymentStatusChip
                      label={product.payment_status_name || (product.payment_status ? (product.payment_status.charAt(0).toUpperCase() + product.payment_status.slice(1)) : 'Pending')}
                      status={product.payment_status}
                      size="small"
                      sx={{ ml: 1.25 }}
                    />
                  </Typography>
                </DetailCard>
              </Grid>
              {product.delivery_challan && (
                <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                  <DetailCard 
                    elevation={2} 
                    sx={{ 
                      backgroundColor: 'background.paper', 
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, 
                      borderRadius: 3,
                      p: 2.5
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', mb: 2 }}>
                      <ChallanIcon sx={{ mr: 1, color: theme.palette.primary.main, flexShrink: 0 }} />
                      <Typography variant="h6" component="span" fontWeight={600} sx={{ mb: 0 }}>Delivery Challan Details</Typography>
                    </Box>
                    
                    {product.delivery_challan.vehicle_number && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Vehicle Number:</strong> {product.delivery_challan.vehicle_number}
                      </Typography>
                    )}
                    {product.delivery_challan.vehicle_name && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Vehicle Name:</strong> {product.delivery_challan.vehicle_name}
                      </Typography>
                    )}
                    {product.delivery_challan.driver_name && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Driver Name:</strong> {product.delivery_challan.driver_name}
                      </Typography>
                    )}
                    {product.delivery_challan.dispatch_person_name && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Dispatch Person:</strong> {product.delivery_challan.dispatch_person_name}
                      </Typography>
                    )}

                    {product.order?.order_by && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Order By:</strong> {product.order.order_by}
                      </Typography>
                    )}
                    {product.order?.order_by_entity && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Entity Name:</strong> {product.order.order_by_entity}
                      </Typography>
                    )}
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Kilometer Information
                    </Typography>
                    {product.delivery_challan.start_km !== null && product.delivery_challan.start_km !== undefined && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Start KM:</strong> {product.delivery_challan.start_km}
                      </Typography>
                    )}
                    {product.delivery_challan.end_km !== null && product.delivery_challan.end_km !== undefined && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>End KM:</strong> {product.delivery_challan.end_km}
                      </Typography>
                    )}
                    {product.delivery_challan.total_km !== null && product.delivery_challan.total_km !== undefined && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Total KM:</strong> {product.delivery_challan.total_km}
                      </Typography>
                    )}
                  </DetailCard>
                </Grid>
              )}
              <Grid item xs={12} sx={{ minWidth: 0 }}>
                  
                  {/* Expected Delivery Date (from farmer) */}
                  {product.expected_delivery_date && (
                    <Box sx={{ mt: 2, mb: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Expected Delivery Date (Farmer):</strong>{' '}
                        <Typography component="span" variant="body2" color="primary" fontWeight={600}>
                          {new Date(product.expected_delivery_date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </Typography>
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Actual Delivery Date (set by admin) */}
                  {product.delivery_date && ['delivered', 'returned'].includes(
                    (product.status_category || product.status_name || product.status?.category || product.status?.name || product.status || '').toString().toLowerCase()
                  ) && (
                    <Box sx={{ mt: 2, mb: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Actual Delivery Date:</strong>{' '}
                        <Typography component="span" variant="body2" color="success.main" fontWeight={600}>
                          {(() => {
                            try {
                              const date = new Date(product.delivery_date);
                              return date.toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              });
                            } catch (e) {
                              return product.delivery_date_formatted || product.delivery_date;
                            }
                          })()}
                        </Typography>
                      </Typography>
                    </Box>
                  )}

                  {/* Status reason returned by API (order.reason) **/}
                  {product.reason && (
                    <Box sx={{ mt: 2, mb: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>
                          {(product.status_name || '').toLowerCase() === 'returned' ? 'Return Reason:' : 'Reason:'}
                        </strong>{' '}
                        <Typography component="span" variant="body2" color="text.secondary">
                          {product.reason}
                        </Typography>
                      </Typography>
                    </Box>
                  )}

              </Grid>
              {getAdvancePaymentEntries(product).length > 0 && (
                <Grid item xs={12}>
                  <DetailCard
                    elevation={2}
                    sx={{
                      backgroundColor: 'background.paper',
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      borderRadius: 3,
                      p: 2.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Payment sx={{ mr: 1, color: theme.palette.warning.main }} />
                      <Typography variant="h6" fontWeight={600}>
                        Advance Payment Details
                      </Typography>
                    </Box>
                    {getAdvancePaymentEntries(product).map((entry, index) => {
                      const fields = entry.fields || {}
                      const formatAdvanceDate = (value) => {
                        if (!value) return 'N/A'
                        try {
                          return new Date(value).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })
                        } catch {
                          return value
                        }
                      }
                      return (
                        <Box
                          key={entry.id ?? `advance-${index}`}
                          sx={{
                            p: 2,
                            mb: index < getAdvancePaymentEntries(product).length - 1 ? 2 : 0,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.warning.main, 0.04),
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1 }}>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {entry.is_full_payment ? 'Full Payment' : 'Advance Payment'}
                            </Typography>
                            {entry.method?.name ? (
                              <Chip
                                size="small"
                                label={entry.method.name}
                                color="warning"
                                variant="outlined"
                              />
                            ) : null}
                          </Box>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>{entry.is_full_payment ? 'Total Amount:' : 'Advance Amount:'}</strong>{' '}
                            {formatCurrency(entry.advance_amount)}
                          </Typography>
                          {!entry.is_full_payment && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Remaining Amount:</strong> {formatCurrency(entry.remaining_amount)}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Note:</strong>{' '}
                            {entry.comment != null && String(entry.comment).trim() !== ''
                              ? entry.comment
                              : 'N/A'}
                          </Typography>
                          {/* {entry.transaction_id ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Transaction ID:</strong> {entry.transaction_id}
                            </Typography>
                          ) : null} */}
                          {fields.cheque_number ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Cheque No:</strong> {fields.cheque_number}
                            </Typography>
                          ) : null}
                          {fields.cheque_date ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Cheque Date:</strong> {formatAdvanceDate(fields.cheque_date)}
                            </Typography>
                          ) : null}
                          {fields.cheque_amount ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Cheque Amount:</strong> {formatCurrency(fields.cheque_amount)}
                            </Typography>
                          ) : null}
                          {fields.cash_receipt_number ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Cash Receipt No:</strong> {fields.cash_receipt_number}
                            </Typography>
                          ) : null}
                          {fields.receiver_name ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Receiver Name:</strong> {fields.receiver_name}
                            </Typography>
                          ) : null}
                          {fields.transaction_id ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Transaction ID:</strong> {fields.transaction_id}
                            </Typography>
                          ) : null}
                          {fields.transaction_reference_no ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Reference No:</strong> {fields.transaction_reference_no}
                            </Typography>
                          ) : null}
                          {fields.sender_bank_name ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Sender Bank:</strong> {fields.sender_bank_name}
                            </Typography>
                          ) : null}
                          {fields.bank_name_branch ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Bank Branch:</strong> {fields.bank_name_branch}
                            </Typography>
                          ) : null}
                          {(fields.payment_confirmation_file || fields.cheque_image) && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                              {fields.payment_confirmation_file ? (
                                <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <strong>Payment Confirmation File:</strong>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => window.open(fields.payment_confirmation_file, '_blank', 'noopener,noreferrer')}
                                  >
                                    View Confirmation
                                  </Button>
                                </Typography>
                              ) : null}
                              {fields.cheque_image ? (
                                <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <strong>Payment Confirmation File:</strong>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => window.open(fields.cheque_image, '_blank', 'noopener,noreferrer')}
                                  >
                                    View Cheque
                                  </Button>
                                </Typography>
                              ) : null}
                            </Box>
                          )}
                        </Box>
                      )
                    })}
                  </DetailCard>
                </Grid>
              )}

              {/* Delivery Challan Details - Show whenever delivery challan exists */}
              {product.order && (
                <Grid item xs={12}>
                  <DetailCard
                    elevation={2}
                    sx={{
                      backgroundColor: 'background.paper',
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      borderRadius: 3,
                      p: 2.5
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <ShoppingCart sx={{ mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="h6" fontWeight={600}>
                        Order Items ({product.order.total_items || 1})
                      </Typography>
                    </Box>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, width: '80px' }}>Sr. No.</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Quantity</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>GST Rate</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow sx={{ '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) } }}>
                            <TableCell>1</TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {product.product_name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                                title={product.category_name}
                              >
                                {product.category_name || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {product.sku || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{formatCurrency(product.discounted_price)}</TableCell>
                            <TableCell align="right">{Number(product.quantity || 0)}</TableCell>
                            <TableCell align="right">
                              {product.order.gst_amount > 0 && product.order.subtotal > 0
                                ? `${((product.order.gst_amount / product.order.subtotal) * 100).toFixed(2)}%`
                                : '0%'}
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                {formatCurrency(Number(product.discounted_price || 0) * Number(product.quantity || 0))}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Total Items:</strong> {product.order.total_items || 1}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Total Quantity:</strong> {Number(product.order.total_quantity || product.quantity || 0)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Subtotal:</strong> {formatCurrency(product.order.subtotal)}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>GST Amount:</strong> {formatCurrency(product.order.gst_amount)}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Delivery Charge:</strong> {formatCurrency(product.order.delivery_charge)}
                          </Typography>
                          {Number(product.order.discount_amount || 0) > 0 && (
                            <Typography variant="body2" color="success.main" sx={{ mb: 0.5 }}>
                              <strong>Discount:</strong> -{formatCurrency(product.order.discount_amount)}
                            </Typography>
                          )}
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="h6" color="primary.main" fontWeight={700}>
                            Total: {formatCurrency(product.order.total_amount)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </DetailCard>
                </Grid>
              )}

            </Grid>
          </Box>
        </Collapse>
      </StyledTableCell>
    </ExpandableRow>
  );

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      {/* Export Controls */}
      {selectedProducts.length > 0 && (
        <Box sx={{ 
          mb: 2, 
          p: 2, 
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box>
            <Typography variant="body1" fontWeight={600}>
              {selectedProducts.length} product{selectedProducts.length > 1 ? 's' : ''} selected
            </Typography>
            {getEligibleProductsCount() > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1, display: 'block' }}>
                {getEligibleProductsCount()} item Selected
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Bulk Payment Status Update Button - Show prominently in Confirm tab */}
           
            <Button
              variant="contained"
              size="small"
              startIcon={<FileDownload />}
              onClick={openActionsMenu}
              sx={{ textTransform: 'none' }}
            >
              Actions
            </Button>
            <Menu
              anchorEl={actionsAnchorEl}
              open={isActionsMenuOpen}
              onClose={closeActionsMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <ListSubheader disableSticky>Export</ListSubheader>
              <MenuItem onClick={() => { closeActionsMenu(); onExport && onExport('excel'); }}>
                <ListItemIcon>
                  <FileDownload fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Export to Excel" />
              </MenuItem>
              <MenuItem onClick={() => { closeActionsMenu(); onExport && onExport('csv'); }}>
                <ListItemIcon>
                  <FileDownload fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Export to CSV" />
              </MenuItem>
              <Divider />
              <ListSubheader disableSticky>Documents ZIP</ListSubheader>
              <MenuItem 
                onClick={() => { closeActionsMenu(); onZipDownload && onZipDownload('invoice'); }}
                disabled={shouldDisableDocumentsAndEmail()}
              >
                <ListItemIcon>
                  <ReceiptIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Invoices ZIP" />
              </MenuItem>
              <MenuItem 
                onClick={() => { closeActionsMenu(); onZipDownload && onZipDownload('challan'); }}
                disabled={shouldDisableDocumentsAndEmail()}
              >
                <ListItemIcon>
                  <ChallanIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Challans ZIP" />
              </MenuItem>
              <MenuItem 
                onClick={() => { closeActionsMenu(); onZipDownload && onZipDownload('both'); }}
                disabled={shouldDisableDocumentsAndEmail()}
              >
                <ListItemIcon>
                  <DownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Both ZIP" />
              </MenuItem>
              <Divider />
              <ListSubheader disableSticky>Email</ListSubheader>
              <MenuItem 
                onClick={handleOpenEmailDialog}
                disabled={shouldDisableDocumentsAndEmail()}
              >
                <ListItemIcon>
                  <EmailIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Send via Email" />
              </MenuItem>
              <Divider />
              <ListSubheader disableSticky>Operations</ListSubheader>
              <MenuItem
                onClick={handleOpenBulkPaymentStatusModal}
                disabled={selectedProducts.length === 0 || isBulkPaymentUpdateDisabled()}
              >
                <ListItemIcon>
                  <Payment fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Bulk Payment Status Update" />
              </MenuItem>
              <MenuItem
                onClick={() => { closeActionsMenu(); handleBulkDeliveryChallan(); }}
                disabled={
                  selectedProducts.length === 0 || 
                  ['Returned', 'Cancelled'].includes(statusFilter)
                }
              >
                <ListItemIcon>
                  <DeliveryIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={statusFilter === 'Dispatch' ? 'Status Update' : 'Bulk Delivery Status'} />
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      )}

      <TableContainer component={Paper} sx={{ maxHeight: 600, overflowX: "auto",
          /* Firefox */
            scrollbarWidth: "thin",
            scrollbarColor:
              theme.palette.mode === "dark"
                ? "#555 #1e1e1e"
                : "#c1c1c1 #f1f1f1",

            /* Chrome, Edge, Safari */
            "&::-webkit-scrollbar": {
              height: 8,
              width: 8,
            },
            "&::-webkit-scrollbar-track": {
              background:
                theme.palette.mode === "dark"
                  ? "#1e1e1e"
                  : "#f1f1f1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              background:
                theme.palette.mode === "dark"
                  ? "#555"
                  : "#c1c1c1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background:
                theme.palette.mode === "dark"
                  ? "#777"
                  : "#a8a8a8",
            },
       }}>
        <Table
          stickyHeader
          sx={{
            width: "100%",
            tableLayout: "fixed",
            "& .MuiTableCell-root": {
              verticalAlign: "middle",
            },
          }}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: (theme) => theme.palette.grey[200] }}>
              <StyledTableCell sx={{ width: 50, minWidth: 50, backgroundColor: (theme) => theme.palette.grey[200] }}>
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={selectedProducts.length > 0 && !isAllSelected}
                  onChange={handleSelectAllToggle}
                  color="primary"
                />
              </StyledTableCell>
              <StyledTableCell align="center" sx={{ width: 80, minWidth: 80, backgroundColor: (theme) => theme.palette.grey[200] }}>Sr. No.</StyledTableCell>
              <StyledTableCell align="center" sx={{ width: 155, minWidth: 155, backgroundColor: (theme) => theme.palette.grey[200] }}>Order ID</StyledTableCell>
              <StyledTableCell align="center" sx={{ width: 150, minWidth: 130, backgroundColor: (theme) => theme.palette.grey[200] }}>Customer Name</StyledTableCell>
              <StyledTableCell align="center" sx={{ width: 200, minWidth: 140, whiteSpace: 'normal', backgroundColor: (theme) => theme.palette.grey[200] }}>Company Name</StyledTableCell>
              <StyledTableCell align="center" sx={{ width: 110, minWidth: 100, backgroundColor: (theme) => theme.palette.grey[200] }}>Status</StyledTableCell>
              <StyledTableCell align="center" sx={{ width: 90, minWidth: 88, backgroundColor: (theme) => theme.palette.grey[200] }}>Total Amount</StyledTableCell>
              <StyledTableCell align="center" sx={{
              width: 180,
              minWidth: 180,
              position: "sticky",
              right: 0,
              zIndex: 5,
              backgroundColor: (theme) => theme.palette.grey[200], // 🔑 ensures no overlap
            }}>Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ border: 'none' }}>
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                    <Typography color="text.secondary">
                      No Products found
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((product, index) => {
                const isExpanded = expandedRows.has(product.id)
                const isSelected = isProductSelected(product.id)
                
                return (
                  <React.Fragment key={product.id}>
                    <StyledTableRow hover selected={isSelected} onClick={() => toggleRow(product.id)}>
                      <StyledTableCell
                        onClick={(e) => e.stopPropagation()}
                        sx={{ width: 50, minWidth: 50 }}
                      >
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => handleProductSelection(product.id, e.target.checked)}
                          color="primary"
                          size="small"
                        />
                      </StyledTableCell>
                      <StyledTableCell align="center" sx={{ width: 80, minWidth: 80 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          {(currentPage - 1) * pageSize + index + 1}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center" sx={{ width: 155, minWidth: 155 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRow(product.id);
                            }}
                            sx={{
                              p: 0.25,
                              transition: "transform 0.2s",
                              transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                            }}
                          >
                            <KeyboardArrowRightIcon fontSize="small" />
                          </IconButton>
                          <Typography variant="body2" style={{ fontSize: "14px" }} fontWeight={500}>
                            {product.order_id}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                          {product.order_date_formatted || (product.created_at ? new Date(product.created_at).toLocaleDateString() : "Date N/A")}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center" sx={{ width: 150, minWidth: 130 }}>
                        <Typography variant="body2">{product.customer_name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {product.mobile_number}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center" sx={{ width: 200, minWidth: 140, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        <Typography variant="body2">
                          {product.company_name}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center" sx={{ width: 110, minWidth: 100 }}>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <StatusChip 
                            status={product.status_category}
                            component="div"
                          >
                            {product.status_name || (product.status ? (product.status.charAt(0).toUpperCase() + product.status.slice(1)) : '')}
                          </StatusChip>
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell align="center" sx={{ width: 90, minWidth: 88 }}>
                        ₹{product.discounted_price}
                      </StyledTableCell>
                      <StyledTableCell
                        align="center"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                        width: 180,
                        minWidth: 180,
                        position: "sticky",
                        right: 0,
                        zIndex: 4,
                        backgroundColor: theme.palette.background.paper,
                      }}>
                        <Box display="flex" gap={1} sx={{ justifyContent: "center", alignItems: "center", width: "100%" }}>
                         
                          <Tooltip
                            title={
                              ['cancelled', 'returned'].includes((statusFilter || '').toLowerCase())
                                ? 'Status update is not available on Cancelled or Returned tabs'
                                : !shouldEnableButton?.('update_status')
                                  ? "You don't have permission to update status"
                                  : "Update Status"
                            }
                          >
  <span>
    <IconButton 
      size="small"
      onClick={(e) => {
        e.stopPropagation()
        handleOpenStatusModal(product)
      }}
      disabled={
        !shouldEnableButton?.('update_status') ||
        ['cancelled', 'returned'].includes((statusFilter || '').toLowerCase())
      }
      sx={{
        opacity:
          !shouldEnableButton?.('update_status') ||
          ['cancelled', 'returned'].includes((statusFilter || '').toLowerCase())
            ? 0.5
            : 1
      }}
    >
      <UpdateIcon fontSize="small" />
    </IconButton>
  </span>
</Tooltip>

<Tooltip title={getPaymentUpdateTooltip(product)}>
  <span>
    <IconButton 
      size="small"
      onClick={(e) => {
        e.stopPropagation()
        openPaymentModalForProduct(product)
      }}
      disabled={
        !shouldEnableButton?.('update_payment') ||
        isPaymentUpdateDisabled(product)
      }
      sx={{
        opacity:
          !shouldEnableButton?.('update_payment') ||
          isPaymentUpdateDisabled(product)
            ? 0.5
            : 1,
      }}
    >
      <Payment fontSize="small" />
    </IconButton>
  </span>
</Tooltip>

{/* Send Email — always takes up space, hidden on placed/confirmed/processing */}
<span style={{
  visibility: ['placed', 'confirmed', 'processing'].includes((statusFilter || '').toLowerCase())
    ? 'hidden' : 'visible'
}}>
  <Tooltip title={!shouldEnableButton?.('send_email') ? "You don't have permission to send email" : "Send Email"}>
    <span>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation()
          openSingleOrderEmail(product)
        }}
        disabled={!shouldEnableButton?.('send_email')}
        sx={{ opacity: !shouldEnableButton?.('send_email') ? 0.5 : 1 }}
      >
        <EmailIcon fontSize="small" />
      </IconButton>
    </span>
  </Tooltip>
</span>

{product.invoice && product.invoice.path && (
  <Tooltip title={!shouldEnableButton?.('view_invoice') ? "You don't have permission to view invoice" : "View Invoice"}>
    <span>
      <IconButton 
        size="small"
        onClick={(e) => {
          e.stopPropagation()
          handleViewInvoice(product)
        }}
        disabled={!shouldEnableButton?.('view_invoice')}
        color="primary"
        sx={{ opacity: !shouldEnableButton?.('view_invoice') ? 0.5 : 1 }}
      >
        <ReceiptIcon fontSize="small" />
      </IconButton>
    </span>
  </Tooltip>
)}

{product.delivery_challan && product.delivery_challan.path && (
  <Tooltip title={!shouldEnableButton?.('view_delivery_challan') ? "You don't have permission to view delivery challan" : "View Delivery Challan"}>
    <span>
      <IconButton 
        size="small"
        onClick={(e) => {
          e.stopPropagation()
          handleViewDeliveryChallan(product)
        }}
        disabled={!shouldEnableButton?.('view_delivery_challan')}
        color="secondary"
        sx={{ opacity: !shouldEnableButton?.('view_delivery_challan') ? 0.5 : 1 }}
      >
        <ChallanIcon fontSize="small" />
      </IconButton>
    </span>
  </Tooltip>
)}
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                    <ProductDetailsRow product={product} />
                  </React.Fragment>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Enhanced Status Update Modal */}
      <Dialog 
        open={statusUpdateModal.open} 
        onClose={handleCloseStatusModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UpdateIcon color="primary" />
            <Typography variant="h6">Update Order Status</Typography>
          </Box>
          {statusUpdateModal.order && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Order ID: {statusUpdateModal.order.order_id}
            </Typography>
          )}
        </DialogTitle>
        
        <DialogContent
        sx={{ /* Firefox */
          scrollbarWidth: "thin",
          scrollbarColor:
            theme.palette.mode === "dark"
              ? "#555 #1e1e1e"
              : "#c1c1c1 #f1f1f1",
          /* Chrome, Edge, Safari */
          "&::-webkit-scrollbar": {
            width: 8,
          },
          "&::-webkit-scrollbar-track": {
            background:
              theme.palette.mode === "dark" ? "#1e1e1e" : "#f1f1f1",
            borderRadius: 8,
          },
          "&::-webkit-scrollbar-thumb": {
            background:
              theme.palette.mode === "dark" ? "#555" : "#c1c1c1",
            borderRadius: 8,
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background:
              theme.palette.mode === "dark" ? "#777" : "#a8a8a8",
          },}}>
          {statusUpdateModal.challanError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {statusUpdateModal.challanError}
            </Alert>
          )}

          {statusUpdateModal.isCreatingChallan && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Creating delivery challan...
              </Box>
            </Alert>
          )}

          <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Order Status</InputLabel>
          <Select
            value={statusUpdateModal.order?.status_name || ''}
            label="Order Status"
            onChange={(e) => {
              const selectedOption = statusOptions.find(opt => opt.name === e.target.value);
              handleStatusModalChange('order', { 
                ...statusUpdateModal.order, 
                status_name: e.target.value,
                status: (e.target.value || '').toString().toLowerCase(),
                status_category: selectedOption?.category || ''
              });
            }}
            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
          >
            {statusOptions.map((option) => {
              // Use original status for validation, not the auto-selected next status
              // This ensures we only show the next sequential status, not skip ahead
              const originalStatusCategory = (statusUpdateModal.originalStatus?.category || '').toLowerCase();
              const originalStatusName = statusUpdateModal.originalStatus?.name || '';
              
              // Get current status category and name - try multiple sources (for display)
              let currentStatusCategory = (statusUpdateModal.order?.status_category || '').toLowerCase();
              const currentStatusName = statusUpdateModal.order?.status_name || statusUpdateModal.order?.status?.name || '';
              
              // If category not available, find it from statusOptions by name
              if (!currentStatusCategory && currentStatusName) {
                const currentStatusOption = statusOptions.find(opt => opt.name === currentStatusName);
                currentStatusCategory = currentStatusOption?.category?.toLowerCase() || '';
              }
              
              // Get statuses to disable based on ORIGINAL status (not the auto-selected one)
              const statusesToDisable = getStatusesToDisable(originalStatusCategory, originalStatusName);
              
              // Check payment status - Outstanding/Credit payment allows orders to proceed
              const paymentStatus = (statusUpdateModal.order?.payment_status?.name || statusUpdateModal.order?.payment_status || '').toLowerCase();
              const isPaid = paymentStatus === 'paid' || paymentStatus === 'outstanding' || paymentStatus === 'credit';
              
              // If not paid/outstanding/credit, only allow "Orders" status (name: "Orders")
              const isOrdersStatus = option.name === 'Orders';
              const shouldDisableByPayment = !isPaid && !isOrdersStatus;
              
              // Check if this option should be disabled
              const optionCategory = (option.category || '').toLowerCase();
              // Only disable if category is valid and in the disabled list
              const shouldDisableBySequence = optionCategory && statusesToDisable.includes(optionCategory);
              const shouldDisable = shouldDisableByPayment || shouldDisableBySequence;
              
              return (
                <MenuItem 
                  key={option.id || option.name} 
                  value={option.name}
                  disabled={shouldDisable || updatingStatus || statusUpdateModal.isCreatingChallan}
                  sx={shouldDisable ? { 
                    opacity: 0.5,
                    backgroundColor: theme.palette.grey[100],
                    '&:hover': {
                      backgroundColor: theme.palette.grey[100],
                      cursor: 'not-allowed'
                    }
                  } : {}}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    {getCategoryIcon(option.category)}
                    <StatusChip 
                      status={option.category}
                      component="span"
                    >
                      {option.name}
                    </StatusChip>
                    {statusUpdateModal.order?.status_name === option.name && (
                      <CheckCircle 
                        color="primary" 
                        sx={{ 
                          ml: 'auto', 
                          fontSize: '16px' 
                        }} 
                      />
                    )}
                    {shouldDisableByPayment && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                        Payment required
                      </Typography>
                    )}
                    {shouldDisableBySequence && !shouldDisableByPayment && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                        Sequential only
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

          {/* Payment Status Warning - Outstanding payment is allowed */}
          {statusUpdateModal.order && (() => {
            const paymentStatus = (statusUpdateModal.order?.payment_status?.name || statusUpdateModal.order?.payment_status || '').toLowerCase();
            const isPaid = paymentStatus === 'paid' || paymentStatus === 'outstanding' || paymentStatus === 'credit';
            const selectedStatus = statusUpdateModal.order?.status_name || '';
            const isOrdersStatus = selectedStatus === 'Orders';
            
            // Only show warning if payment is not paid/outstanding/credit AND status is not "Orders"
            if (!isPaid && !isOrdersStatus) {
              return (
                <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Payment Required:</strong> This order must be paid before it can be moved to any status other than "Orders". Please update the payment status first.
                  </Typography>
                </Alert>
              );
            }
            return null;
          })()}

          {/* Conditional fields for shipping */}
          {(() => {
            const selectedStatusName = statusUpdateModal.order?.status_name || '';
            const selectedOption = statusOptions.find(opt => opt.name === selectedStatusName);
            const statusCategoryForShipping = (selectedOption?.category || '').toLowerCase();
            return statusCategoryForShipping === 'shipped';
          })() && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ 
                p: 2, 
                backgroundColor: alpha(theme.palette.info.main, 0.1),
                borderRadius: 2,
                mb: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <DeliveryIcon color="info" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Delivery Information Required
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Please provide delivery details to create the delivery challan
                </Typography>
              </Box>

                              <Grid container spacing={2}>
                

               <Grid item xs={12}>
                                <FormControl fullWidth required>
                                  <InputLabel>Order By</InputLabel>
                                  <Select
                                    value={statusUpdateModal.orderBy}
                                    onChange={(e) => handleStatusModalChange('orderBy', e.target.value)}
                                    disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                                    label="Order By"
                                  >
                                    <MenuItem value="">
                                      <em>Select order by</em>
                                    </MenuItem>
                                    {orderByChoices.map((choice) => (
                                      <MenuItem key={choice.key} value={choice.key}>
                                        {choice.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
              
                               {/* Entity Name */}
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Delivery Partner Name"
                                value={statusUpdateModal.entityName}
                                onChange={(e) => {
                                  const lettersOnly = e.target.value.replace(/[^A-Za-z ]/g, '');
                                  handleStatusModalChange('entityName', lettersOnly);
                                }}
                                disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                                required
                                placeholder="Enter delivery partner name"
                              />
                            </Grid>
                                                      

                            <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Driver/Authorized Name"
                          value={statusUpdateModal.driverName}
                          onChange={(e) => {
                            const lettersOnly = e.target.value.replace(/[^A-Za-z ]/g, '');
                            handleStatusModalChange('driverName', lettersOnly);
                          }}
                          disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                          required
                          placeholder="Driver/Authorized Name"
                          error={statusUpdateModal.challanError.includes('Driver name')}
                        />
                      </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Vehicle No / Reference No"
                    value={statusUpdateModal.vehicleNo}
                    onChange={(e) => {
                      const isTruckOrderBy = String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck';
                      handleStatusModalChange(
                        'vehicleNo',
                        isTruckOrderBy ? formatVehicleReferenceNo(e.target.value) : e.target.value
                      );
                    }}
                    disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                    required={String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck'}
                    placeholder="Vehicle No / Reference No"
                    inputProps={
                      String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck'
                        ? { maxLength: 13 }
                        : undefined
                    }
                    error={
                      (String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck' &&
                        isVehicleReferenceNoInvalid(statusUpdateModal.vehicleNo)) ||
                      statusUpdateModal.challanError.includes('vehicle number')
                    }
                    helperText={
                      String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck' &&
                      isVehicleReferenceNoInvalid(statusUpdateModal.vehicleNo)
                        ? VEHICLE_REFERENCE_NO_ERROR
                        : ''
                    }
                  />
                </Grid>
                  
                  {/* Start KM - only show when Order By is "Truck" */}
                  {String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck' && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        // required
                        label="Start KM"
                        type="number"
                        value={statusUpdateModal.start_km}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^[1-9]\d*$/.test(value)) {
                            handleStatusModalChange('start_km', value);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onWheel={(e) => e.currentTarget.blur()}
                        disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        placeholder="Enter starting kilometer reading"
                        inputProps={{ min: 1, step: 1 }}
                      />
                    </Grid>
                  )}  
                <Grid item xs={12}>
                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={statusUpdateModal.useCustomDispatchPerson}
                          onChange={(e) => handleStatusModalChange('useCustomDispatchPerson', e.target.checked)}
                          disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        />
                      }
                      label="Add custom dispatch person"
                    />
                    {statusUpdateModal.useCustomDispatchPerson ? (
                      <TextField
                        fullWidth
                        label="Custom Dispatch Person Name"
                        value={statusUpdateModal.customDispatchPerson}
                        onChange={(e) => handleStatusModalChange('customDispatchPerson', e.target.value)}
                        disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        required
                        placeholder="Enter custom dispatch person name"
                        sx={{ mt: 1 }}
                      />
                    ) : (
                      <FormControl fullWidth required sx={{ mt: 1 }}>
                        <InputLabel>Dispatch Person Name</InputLabel>
                        <Select
                          value={statusUpdateModal.dispatchPersonName}
                          label="Dispatch Person Name"
                          onChange={(e) => handleStatusModalChange('dispatchPersonName', e.target.value)}
                          disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        >
                          <MenuItem value="">
                            <em>Select dispatch person</em>
                          </MenuItem>
                          {dispatchPersons.map((person) => (
                            <MenuItem key={person.id} value={person.name}>
                              {person.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Delivery Date"
                    type="date"
                    value={statusUpdateModal.shippedDate}
                    onChange={(e) => handleStatusModalChange('shippedDate', e.target.value)}
                    disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                    required
                    InputLabelProps={{ shrink: true }}
                      inputProps={{
                    min: new Date().toISOString().split("T")[0], // 🔑 disables all past dates
                  }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Email Addresses (Optional)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={statusUpdateModal.emailInput}
                        onChange={(e) => handleStatusModalChange('emailInput', e.target.value)}
                        disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        placeholder="Enter email address"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addEmailToStatusModal();
                          }
                        }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={addEmailToStatusModal}
                        disabled={!statusUpdateModal.emailInput?.trim() || updatingStatus || statusUpdateModal.isCreatingChallan}
                      >
                        Add
                      </Button>
                    </Box>
                    {statusUpdateModal.emails?.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {statusUpdateModal.emails?.map((email, index) => (
                          <Chip
                            key={index}
                            label={email}
                            onDelete={() => removeEmailFromStatusModal(email)}
                            size="small"
                            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                          />
                        ))}
                      </Box>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Delivery challan will be sent to these email addresses
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={statusUpdateModal.generateInvoice}
                        onChange={(e) => handleStatusModalChange('generateInvoice', e.target.checked)}
                        disabled={
                          updatingStatus ||
                          statusUpdateModal.isCreatingChallan ||
                          (['processing'].includes((statusFilter || '').toLowerCase()) &&
                            !shouldEnableButton?.('generate_invoice_along_with_delivery_challan'))
                        }
                      />
                    }
                    label="Generate Invoice along with Delivery Challan"
                  />
                </Grid>
              </Grid>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                * A delivery challan will be created automatically with these details
                {statusUpdateModal.generateInvoice && (
                  <><br />* An invoice will also be generated for this order.</>
                )}
              </Typography>
            </Box>
          )}

          {(statusUpdateModal.order?.status_name || statusUpdateModal.order?.status?.name) === 'Delivered' && (() => {
            const truckOrderWithStartKm = getTruckOrderWithStartKm([statusUpdateModal.order]);
            const deliveryChallan = truckOrderWithStartKm?.delivery_challan;
            if (!deliveryChallan) return null;

            const driverName = statusUpdateModal.driverName || deliveryChallan.driver_name || '';
            const vehicleNo =
              statusUpdateModal.vehicleNo ||
              deliveryChallan.vehicle_number ||
              deliveryChallan.vehicle_no ||
              deliveryChallan.vehicle_name ||
              '';
            const startKm =
              statusUpdateModal.start_km ||
              (
                deliveryChallan.start_km !== null && deliveryChallan.start_km !== undefined
                  ? String(deliveryChallan.start_km)
                  : ''
              );

            return (
              <Box sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Driver Name"
                      value={driverName}
                      disabled
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Vehicle No / Reference No"
                      value={vehicleNo}
                      disabled
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Start KM"
                      value={startKm}
                      disabled
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="End KM"
                      required
                      type="number"
                      value={statusUpdateModal.end_km || ''}
                      onChange={(e) => handleStatusModalChange('end_km', e.target.value)}
                      disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                      placeholder="Enter ending kilometer reading"
                      error={(() => {
                        const parsedStartKm = parseFloat(startKm);
                        const endKm = parseFloat(statusUpdateModal.end_km);
                        const hasEndKm = String(statusUpdateModal.end_km || '').trim() !== '';
                        return !hasEndKm || (!isNaN(parsedStartKm) && !isNaN(endKm) && endKm <= parsedStartKm);
                      })()}
                      helperText={(() => {
                        const parsedStartKm = parseFloat(startKm);
                        const endKm = parseFloat(statusUpdateModal.end_km);
                        const hasEndKm = String(statusUpdateModal.end_km || '').trim() !== '';
                        if (!hasEndKm) return 'End KM is required';
                        if (!isNaN(parsedStartKm) && !isNaN(endKm) && endKm <= parsedStartKm) {
                          return 'End KM must be greater than Start KM';
                        }
                        return '';
                      })()}
                    />
                  </Grid>
                </Grid>
              </Box>
            );
          })()}

          

          {/* Conditional field for Cancelled status - Cancellation Reason */}
          {(statusUpdateModal.order?.status_name || statusUpdateModal.order?.status?.name) === 'Cancelled' && (
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Cancellation Reason"
                    required
                    multiline
                    rows={3}
                    value={statusUpdateModal.cancellationReason || ''}
                    onChange={(e) => handleStatusModalChange('cancellationReason', e.target.value)}
                    disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                    placeholder="Enter reason for cancellation"
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Conditional field for Returned status - Return Reason */}
          {(statusUpdateModal.order?.status_name || statusUpdateModal.order?.status?.name) === 'Returned' && (
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Return Reason"
                    required
                    multiline
                    rows={3}
                    value={statusUpdateModal.returnReason || ''}
                    onChange={(e) => handleStatusModalChange('returnReason', e.target.value)}
                    disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                    placeholder="Enter reason for return"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={handleCloseStatusModal}
            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => handleStatusUpdate(
              statusUpdateModal.order?.id,
              statusUpdateModal.order?.status_name,
              statusUpdateModal.order?.order_id,
              statusUpdateModal.emails,
              statusUpdateModal.generateInvoice,
              statusUpdateModal.driverName,
              statusUpdateModal.vehicleNo,
              statusUpdateModal.delivery_date
            )}
            variant="contained"
            disabled={
              updatingStatus || 
              statusUpdateModal.isCreatingChallan ||
              !statusUpdateModal.order?.status_name ||
              (() => {
                const selectedStatusName = statusUpdateModal.order?.status_name || '';
                const selectedOption = statusOptions.find(opt => opt.name === selectedStatusName);
                const statusCategoryForShipping = (selectedOption?.category || '').toLowerCase();
                if (statusCategoryForShipping !== 'shipped') return false;
                const orderBy = String(statusUpdateModal.orderBy || '').trim().toLowerCase();
                const needStartKm = orderBy === 'truck';
                const missingCommon = !statusUpdateModal.orderBy?.trim() || !statusUpdateModal.entityName?.trim() || !statusUpdateModal.driverName.trim() || (needStartKm && !statusUpdateModal.vehicleNo.trim()) || (needStartKm && isVehicleReferenceNoInvalid(statusUpdateModal.vehicleNo));
                const missingStartKm = needStartKm && !(statusUpdateModal.start_km !== undefined && String(statusUpdateModal.start_km).trim());
                const invalidStartKm = needStartKm && Number(statusUpdateModal.start_km) < 1;
                const missingDeliveryDate = !statusUpdateModal.shippedDate?.trim();
                const missingDispatchPerson = statusUpdateModal.useCustomDispatchPerson 
                  ? !statusUpdateModal.customDispatchPerson?.trim()
                  : !statusUpdateModal.dispatchPersonName?.trim();
                return missingCommon || missingStartKm || invalidStartKm || missingDeliveryDate || missingDispatchPerson;
              })() ||
              (() => {
                const selectedStatus = statusUpdateModal.order?.status_name || statusUpdateModal.order?.status?.name || '';
                if (selectedStatus !== 'Delivered') return false;
                const truckOrderWithStartKm = getTruckOrderWithStartKm([statusUpdateModal.order]);
                const deliveryChallan = truckOrderWithStartKm?.delivery_challan;
                if (!deliveryChallan) return false;
                const startKm = parseFloat(statusUpdateModal.start_km || deliveryChallan.start_km);
                const endKm = parseFloat(statusUpdateModal.end_km);
                if (!String(statusUpdateModal.end_km || '').trim()) return true;
                return !isNaN(startKm) && !isNaN(endKm) && endKm <= startKm;
              })() ||
              ((statusUpdateModal.order?.status_name || statusUpdateModal.order?.status?.name) === 'Cancelled' && 
               !statusUpdateModal.cancellationReason?.trim()) ||
              ((statusUpdateModal.order?.status_name || statusUpdateModal.order?.status?.name) === 'Returned' && 
               !statusUpdateModal.returnReason?.trim()) ||
              (() => {
                // Disable if payment is not paid/outstanding/credit and selected status is not "Orders"
                const paymentStatus = (statusUpdateModal.order?.payment_status?.name || statusUpdateModal.order?.payment_status || '').toLowerCase();
                const isPaid = paymentStatus === 'paid' || paymentStatus === 'outstanding' || paymentStatus === 'credit';
                const selectedStatus = statusUpdateModal.order?.status_name || '';
                const isOrdersStatus = selectedStatus === 'Orders';
                return !isPaid && !isOrdersStatus;
              })()
            }
            startIcon={
              updatingStatus || statusUpdateModal.isCreatingChallan ? 
              <CircularProgress size={16} /> : 
              (() => {
                const selectedStatusName = statusUpdateModal.order?.status_name || '';
                const selectedOption = statusOptions.find(opt => opt.name === selectedStatusName);
                const statusCategoryForShipping = (selectedOption?.category || '').toLowerCase();
                return statusCategoryForShipping === 'shipped' ? <DeliveryIcon /> : <UpdateIcon />;
              })()
            }
          >
            {updatingStatus ? 'Updating Status...' : 
             statusUpdateModal.isCreatingChallan ? 'Creating Challan...' :
             (() => {
               const selectedStatusName = statusUpdateModal.order?.status_name || '';
               const selectedOption = statusOptions.find(opt => opt.name === selectedStatusName);
               const statusCategoryForShipping = (selectedOption?.category || '').toLowerCase();
               return statusCategoryForShipping === 'shipped' ? 'Ship Order' : 'Update Status';
             })()}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Status Update Modal */}
      <Dialog
        open={paymentUpdateModal.open}
        onClose={() => {
          if (updatingPayment) return
          setPaymentUpdateModal(closePaymentUpdateModalState())
          setPaymentStatusChoices([])
        }}
        maxWidth={paymentUpdateModal.step === 'payment_details' ? false : 'sm'}
        fullWidth={paymentUpdateModal.step !== 'payment_details'}
        scroll={paymentUpdateModal.step === 'payment_details' ? 'paper' : 'body'}
        PaperProps={
          paymentUpdateModal.step === 'payment_details'
            ? PAYMENT_DETAILS_DIALOG_PAPER_PROPS
            : undefined
        }
      >
        <DialogTitle>
          {paymentUpdateModal.step === 'payment_details'
            ? 'Payment Details'
            : 'Update Payment Status'}
          {paymentUpdateModal.order && (
            <Typography variant="body2" color="text.secondary">
              Order ID: {paymentUpdateModal.order.order_id}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent
          sx={
            paymentUpdateModal.step === 'payment_details'
              ? PAYMENT_DETAILS_DIALOG_CONTENT_SX
              : undefined
          }
        >
          {paymentUpdateModal.paymentError && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
              {paymentUpdateModal.paymentError}
            </Alert>
          )}

          {paymentUpdateModal.step === 'status' && (
            <>
              {isPaymentUpdateDisabled(paymentUpdateModal.order) && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {getPaymentUpdateDisabledReason(paymentUpdateModal.order, statusFilter)}
                </Alert>
              )}
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="single-payment-status-label">Payment Status</InputLabel>
                <Select
                  labelId="single-payment-status-label"
                  value={getOrderPaymentStatusValue(paymentUpdateModal.order)}
                  label="Payment Status"
                  disabled={
                    loadingPaymentStatusChoices ||
                    updatingPayment ||
                    isPaymentUpdateDisabled({
                      ...paymentUpdateModal.order,
                      payment_status: {
                        name: paymentUpdateModal.initialPaymentStatus || '',
                      },
                    })
                  }
                  MenuProps={{
                    disableScrollLock: true,
                    PaperProps: {
                      sx: { maxHeight: 300 },
                    },
                    sx: {
                      zIndex: (theme) => theme.zIndex.modal + 1,
                    },
                  }}
                  onChange={(e) => {
                    const newStatus = e.target.value
                    if (newStatus === 'paid' || newStatus === 'credit') {
                      loadAdvancePaymentMethods()
                    }
                    setPaymentUpdateModal((prev) => ({
                      ...prev,
                      order: {
                        ...prev.order,
                        payment_status: {
                          ...(typeof prev.order?.payment_status === 'object'
                            ? prev.order.payment_status
                            : {}),
                          name: newStatus,
                        },
                      },
                      refundAmount:
                        newStatus === 'refunded'
                          ? String(prev.order?.total_amount ?? prev.refundAmount ?? '')
                          : prev.refundAmount,
                      paymentError: '',
                      ...(newStatus === 'paid'
                        ? getInlinePaidPaymentStepState(prev.orderPaymentDetails)
                        : newStatus === 'credit'
                          ? getCreditPaymentStepState(prev.orderPaymentDetails)
                          : {
                              step: 'status',
                              paymentOption: '',
                              advanceAmount: '',
                              installmentAmount: '',
                              creditNote: '',
                              paymentMethod: '',
                              paymentMethodConfig: null,
                              paymentFields: {},
                              paymentFiles: {},
                            }),
                    }))
                  }}
                >
                  {renderPaymentStatusMenuItems()}
                </Select>
              </FormControl>
              {(paymentUpdateModal.paymentOption === 'credit' ||
                paymentUpdateModal.paymentOption === 'remaining') && (
                <Box sx={{ mt: 2 }}>
                  <PaymentDetailsStepContent
                    idPrefix="bulk-single-payment-inline"
                    order={paymentUpdateModal.order}
                    orderPaymentDetails={paymentUpdateModal.orderPaymentDetails}
                    paymentOption={paymentUpdateModal.paymentOption}
                    advanceAmount={paymentUpdateModal.advanceAmount}
                    onAdvanceAmountChange={(e) =>
                      setPaymentUpdateModal((prev) => ({
                        ...prev,
                        advanceAmount: e.target.value,
                        paymentError: '',
                      }))
                    }
                    installmentAmount={paymentUpdateModal.installmentAmount || ''}
                    onInstallmentAmountChange={(e) => {
                      const value = e.target.value
                      const remaining = Number(
                        paymentUpdateModal.orderPaymentDetails?.remainingAmount ?? 0
                      )
                      if (value !== '' && Number(value) > remaining) {
                        setPaymentUpdateModal((prev) => ({
                          ...prev,
                          paymentError: 'Installment amount cannot exceed the remaining amount',
                        }))
                        return
                      }
                      setPaymentUpdateModal((prev) => ({
                        ...prev,
                        installmentAmount: value,
                        paymentError: '',
                        ...(prev.paymentOption === 'credit'
                          ? {
                              paymentFields: getCreditChequePaymentFields(
                                prev.paymentFields,
                                value,
                                prev.paymentMethodConfig
                              ),
                            }
                          : {}),
                      }))
                    }}
                    creditNote={paymentUpdateModal.creditNote || ''}
                    onCreditNoteChange={(e) =>
                      setPaymentUpdateModal((prev) => ({
                        ...prev,
                        creditNote: e.target.value,
                        paymentError: '',
                      }))
                    }
                    paymentMethod={paymentUpdateModal.paymentMethod}
                    paymentMethodConfig={paymentUpdateModal.paymentMethodConfig}
                    advancePaymentMethods={advancePaymentMethods}
                    loadingAdvancePaymentMethods={loadingAdvancePaymentMethods}
                    onPaymentMethodChange={(e) => {
                      const methodId = e.target.value
                      setPaymentUpdateModal((prev) => {
                        const payload = buildPaymentMethodChangePayload(
                          methodId,
                          prev.orderPaymentDetails
                        )
                        if (
                          prev.paymentOption === 'credit' &&
                          payload.paymentMethodConfig?.type === 'cheque'
                        ) {
                          payload.paymentFields = getCreditChequePaymentFields(
                            payload.paymentFields,
                            prev.installmentAmount ?? '',
                            payload.paymentMethodConfig
                          )
                        }
                        return { ...prev, ...payload }
                      })
                    }}
                    paymentFields={paymentUpdateModal.paymentFields}
                    paymentFiles={paymentUpdateModal.paymentFiles}
                    onFieldChange={(key, value) => {
                      if (key === 'cheque_amount' && paymentUpdateModal.orderPaymentDetails) return
                      setPaymentUpdateModal((prev) => ({
                        ...prev,
                        paymentFields: { ...prev.paymentFields, [key]: value },
                        paymentError: '',
                      }))
                    }}
                    onFileChange={(key, file) =>
                      setPaymentUpdateModal((prev) => ({
                        ...prev,
                        paymentFiles: { ...prev.paymentFiles, [key]: file },
                        paymentError: '',
                      }))
                    }
                    totalLabel="Total"
                  />
                </Box>
              )}
              {getOrderPaymentStatusValue(paymentUpdateModal.order) === 'refunded' && (() => {
            const totalAmount = Number(
              paymentUpdateModal.order?.order?.total_amount ??
              paymentUpdateModal.order?.total_amount ??
              0
            );
            const paidAmountFromOrder = Number(
              paymentUpdateModal.order?.paid_amount ??
              paymentUpdateModal.order?.amount_paid ??
              paymentUpdateModal.order?.advance_payment?.advance_amount ??
              0
            );
            const paidAmount = paidAmountFromOrder > 0 ? paidAmountFromOrder : totalAmount;
            const pendingAmount = Math.max(0, totalAmount - paidAmount);
            const formatAmount = (value) => {
              const amount = Number(value || 0);
              return Number.isFinite(amount) ? amount.toFixed(2) : '0.00';
            };
            const refundInput = Number(paymentUpdateModal.refundAmount);
            const isRefundInvalid =
              !Number.isFinite(refundInput) || refundInput <= 0 || refundInput > totalAmount;

            return (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Refund Amount"
                  type="number"
                  value={paymentUpdateModal.refundAmount ?? ''}
                  placeholder="Enter refund amount"
                  inputProps={{
                    min: 0,
                    max: totalAmount || undefined,
                    step: 0.01,
                  }}
                  error={isRefundInvalid && paymentUpdateModal.refundAmount !== ''}
                  helperText={
                    isRefundInvalid && paymentUpdateModal.refundAmount !== ''
                      ? 'Enter a valid amount greater than 0 and not more than the order total.'
                      : ''
                  }
                  onChange={(e) =>
                    setPaymentUpdateModal((prev) => ({
                      ...prev,
                      refundAmount: e.target.value,
                    }))
                  }
                />
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <TextField
                    label="Total"
                    value={formatAmount(totalAmount)}
                    size="small"
                    fullWidth
                    disabled
                  />
                  <TextField
                    label="Paid"
                    value={formatAmount(paidAmount)}
                    size="small"
                    fullWidth
                    disabled
                  />
                  <TextField
                    label="Pending"
                    value={formatAmount(pendingAmount)}
                    size="small"
                    fullWidth
                    disabled
                  />
                </Box>
              </Box>
            );
          })()}
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={
            paymentUpdateModal.step === 'payment_details'
              ? PAYMENT_DETAILS_DIALOG_ACTIONS_SX
              : { px: 3, pb: 2 }
          }
        >
          <Button
            onClick={() => {
              if (paymentUpdateModal.step === 'payment_details') {
                const previousStatus = (
                  paymentUpdateModal.orderPaymentDetails?.current_payment_status ||
                  getOrderPaymentStatusValue(paymentUpdateModal.order) ||
                  'pending'
                )
                  .toString()
                  .toLowerCase()
                setPaymentUpdateModal((prev) => ({
                  ...prev,
                  step: 'status',
                  paymentOption: '',
                  advanceAmount: '',
                  installmentAmount: '',
                  creditNote: '',
                  paymentMethod: '',
                  paymentFields: {},
                  paymentFiles: {},
                  paymentError: '',
                  order: {
                    ...prev.order,
                    payment_status: {
                      ...(typeof prev.order?.payment_status === 'object'
                        ? prev.order.payment_status
                        : {}),
                      name: previousStatus,
                    },
                  },
                }))
                return
              }
              setPaymentUpdateModal(closePaymentUpdateModalState())
              setPaymentStatusChoices([])
            }}
            disabled={updatingPayment}
            sx={{ textTransform: 'none' }}
          >
            {paymentUpdateModal.step === 'status' ? 'Cancel' : 'Back'}
          </Button>

          {paymentUpdateModal.paymentOption === 'credit' &&
            shouldShowCreditSubmitButton(paymentUpdateModal) && (
            <Button
              variant="contained"
              disabled={updatingPayment || !canSubmitCreditPayment(paymentUpdateModal)}
              onClick={handleConfirmCreditPaymentDetails}
              startIcon={updatingPayment ? <CircularProgress size={16} /> : <Payment />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {updatingPayment ? 'Updating...' : 'Submit Payment'}
            </Button>
          )}

          {paymentUpdateModal.paymentOption === 'remaining' && (
            <Button
              variant="contained"
              disabled={updatingPayment || !canSubmitPaidPayment(paymentUpdateModal)}
              onClick={handleConfirmPaidPaymentDetails}
              startIcon={updatingPayment ? <CircularProgress size={16} /> : <Payment />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {updatingPayment ? 'Updating...' : 'Submit Payment'}
            </Button>
          )}

          {paymentUpdateModal.step === 'status' &&
            getOrderPaymentStatusValue(paymentUpdateModal.order) !== 'paid' &&
            getOrderPaymentStatusValue(paymentUpdateModal.order) !== 'credit' && (
            <Button
              onClick={() =>
                handlePaymentStatusUpdate(
                  paymentUpdateModal.order?.id,
                  getOrderPaymentStatusValue(paymentUpdateModal.order),
                  paymentUpdateModal.order?.order_id,
                  paymentUpdateModal.refundAmount,
                  paymentUpdateModal.order?.total_amount
                )
              }
              variant="contained"
              disabled={
                updatingPayment ||
                isPaymentUpdateDisabled(paymentUpdateModal.order) ||
                (() => {
                  const ps = getOrderPaymentStatusValue(paymentUpdateModal.order)
                  const refundNum = Number(paymentUpdateModal.refundAmount)
                  const maxTotal = Number(paymentUpdateModal.order?.total_amount ?? 0)
                  return (
                    ps === 'refunded' &&
                    (!Number.isFinite(refundNum) || refundNum <= 0 || refundNum > maxTotal)
                  )
                })()
              }
              startIcon={updatingPayment ? <CircularProgress size={16} /> : <Payment />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {updatingPayment ? 'Updating...' : 'Update Payment Status'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Invoice View Modal */}
      <InvoiceModal
        open={invoiceModal.open}
        onClose={handleCloseInvoiceModal}
        maxWidth={false}
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PdfIcon color="error" />
              <Typography variant="h6" fontWeight={600}>
                Invoice - {invoiceModal.product?.product_name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={handleCloseInvoiceModal}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          {invoiceModal.product && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Order ID: {invoiceModal.product.order_id} • Customer: {invoiceModal.product.customer_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {invoiceModal.invoice && invoiceModal.invoice.path && (
            <InvoiceFrame
              src={invoiceModal.invoice.path}
              title="Invoice PDF"
              onError={(e) => {
                console.error('Error loading PDF:', e)
                alert('Error loading invoice PDF. Please try downloading instead.')
              }}
            />
          )}
        </DialogContent>
      </InvoiceModal>

      {/* Delivery Challan View Modal */}
      <InvoiceModal
        open={deliveryChallanModal.open}
        onClose={handleCloseDeliveryChallanModal}
        maxWidth={false}
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ChallanIcon color="secondary" />
              <Typography variant="h6" fontWeight={600}>
                Delivery Challan - {deliveryChallanModal.product?.product_name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={handleCloseDeliveryChallanModal}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          {deliveryChallanModal.product && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Order ID: {deliveryChallanModal.product.order_id} • Customer: {deliveryChallanModal.product.customer_name}
              {deliveryChallanModal.challan?.vehicle_number && (
                <> • Vehicle: {deliveryChallanModal.challan.vehicle_number}</>
              )}
              {deliveryChallanModal.challan?.delivery_date && (
                <> • Date: {new Date(deliveryChallanModal.challan.delivery_date).toLocaleDateString()}</>
              )}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {deliveryChallanModal.challan && deliveryChallanModal.challan.path && (
            <InvoiceFrame
              src={deliveryChallanModal.challan.path}
              title="Delivery Challan PDF"
              onError={(e) => {
                console.error('Error loading PDF:', e)
                alert('Error loading delivery challan PDF. Please try downloading instead.')
              }}
            />
          )}
        </DialogContent>
      </InvoiceModal>

      {/* Bulk Delivery Challan Modal */}
      <Dialog
        open={bulkChallanModal.open}
        onClose={handleCloseBulkChallanModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeliveryIcon color="secondary" />
            <Typography variant="h6" fontWeight={600}>
              Bulk Status Update & Delivery Management
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Update status for {getSelectedProductsCount()} selected orders. For "Dispatch" status, delivery challans will be created.
          </Typography>
        </DialogTitle>

        <DialogContent>
          {bulkChallanModal.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {bulkChallanModal.error}
            </Alert>
          )}

          {bulkChallanModal.isCreating && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Creating bulk delivery challan...
              </Box>
            </Alert>
          )}

          {(() => {
            // Check payment status for all selected orders
            const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
            const unpaidOrders = selectedOrders.filter(order => {
              const paymentStatus = (order.payment_status?.name || order.payment_status_name || order.payment_status || '').toLowerCase();
              const isPaid = paymentStatus === 'paid' || paymentStatus === 'outstanding';
              return !isPaid;
            });
            
            if (unpaidOrders.length > 0) {
              return (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Payment Required
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5, mb: 2 }}>
                    {unpaidOrders.length} of {selectedOrders.length} selected order{selectedOrders.length > 1 ? 's' : ''} must be paid before status can be updated. 
                    Please update the payment status first. Only unpaid orders will be updated.
                  </Typography>
                </Alert>
              );
            }
            return null;
          })()}

          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Update Status To</InputLabel>
                  <Select
                    value={
                      // If Dispatch tab is active and Dispatch is selected, clear it
                      (statusFilter === 'Dispatch' && bulkChallanModal.selectedStatus === 'Dispatch') 
                        ? '' 
                        : (bulkChallanModal.selectedStatus || '')
                    }
                    label="Update Status To"
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      const selectedOption = statusOptions.find(opt => opt.name === selectedValue);
                      
                      // Prevent selecting Dispatch when Dispatch tab is active
                      if (statusFilter === 'Dispatch' && selectedValue === 'Dispatch') {
                        return; // Don't allow selection
                      }
                      
                      handleBulkChallanInputChange('selectedStatus', selectedValue);
                      handleBulkChallanInputChange('selectedStatusCategory', selectedOption?.category || '');
                    }}
                    disabled={false}
                  >
                    {statusOptions.map((option) => {
                      const optionName = (option.name || '').toLowerCase();
                      const optionCategory = (option.category || '').toLowerCase();
                      const isCancelled = optionName === 'cancelled' || optionCategory === 'cancelled';
                      const isProcessing = optionName === 'processing' || optionCategory === 'processing';
                      const isDispatch = optionName === 'dispatch' || optionCategory === 'dispatch' || optionCategory === 'shipped';
                      const isDelivered = optionName === 'delivered' || optionCategory === 'delivered';
                      const isReturned = optionName === 'returned' || optionCategory === 'returned';
                      const currentTab = (statusFilter || '').toLowerCase();

                      let shouldEnable = false;
                      if (currentTab === 'confirmed') {
                        shouldEnable = isProcessing || isCancelled;
                      } else if (currentTab === 'processing') {
                        shouldEnable = isDispatch || isCancelled;
                      } else if (currentTab === 'dispatch') {
                        shouldEnable = isDelivered || isCancelled;
                      } else if (currentTab === 'delivered') {
                        shouldEnable = isReturned;
                      } else {
                        // Fallback for mixed/default tabs while preserving our Delivered->Returned rule.
                        shouldEnable = isProcessing || isCancelled || isReturned;
                      }

                      const shouldDisable = !shouldEnable;
                      
                      return (
                        <MenuItem 
                          key={option.id || option.name} 
                          value={option.name}
                          disabled={shouldDisable}
                          sx={shouldDisable ? { 
                            opacity: 0.5,
                            backgroundColor: theme.palette.grey[100],
                            '&:hover': {
                              backgroundColor: theme.palette.grey[100],
                              cursor: 'not-allowed'
                            }
                          } : {}}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            {getCategoryIcon(option.category)}
                            <StatusChip 
                              status={option.category}
                              component="span"
                            >
                              {option.name}
                            </StatusChip>
                            {shouldDisable && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                                Not available
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Show delivery fields only for Dispatch status */}
              {bulkChallanModal.selectedStatus === 'Dispatch' && (
                <>
                  <Grid item xs={12}>
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: alpha(theme.palette.warning.main, 0.1),
                      borderRadius: 2,
                      mb: 2
                    }}>
                      <Typography variant="subtitle2" fontWeight={600} color="warning.main">
                        Delivery Information Required for Dispatch Status
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Please provide delivery details to create delivery challans
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                                <FormControl fullWidth required>
                                  <InputLabel>Order By</InputLabel>
                                  <Select
                                    value={bulkChallanModal.orderBy}
                                    onChange={(e) => handleBulkChallanInputChange('orderBy', e.target.value)}
                                    disabled={bulkChallanModal.isCreating}
                                    label="Order By"
                                  >
                                    <MenuItem value="">
                                      <em>Select order by</em>
                                    </MenuItem>
                                    {orderByChoices.map((choice) => (
                                      <MenuItem key={choice.key} value={choice.key}>
                                        {choice.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>

                              {/* Entity Name */}
                              <Grid item xs={12}>
                                <FormControl fullWidth required>
                                  {/* <InputLabel>Delivery Partner Name</InputLabel> */}
                                  <TextField
                                    value={bulkChallanModal.entityName}
                                    onChange={(e) => handleBulkChallanInputChange('entityName', e.target.value)}
                                    disabled={loadingBulkEntities || bulkChallanModal.isCreating}
                                    label="Delivery Partner Name"
                                  />  
                                  {/* <Select
                                    value={bulkChallanModal.entityName}
                                    label="Delivery Partner Name"
                                    onChange={(e) => handleBulkChallanInputChange('entityName', e.target.value)}
                                    disabled={loadingBulkEntities || bulkChallanModal.isCreating}
                                  >
                                    <MenuItem value="">
                                      <em>Select delivery partner</em>
                                    </MenuItem>
                                    {bulkEntityOptions.map((entity) => (
                                      <MenuItem key={entity} value={entity}>{entity}</MenuItem>
                                    ))}
                                  </Select> */}
                                </FormControl>
                              </Grid>
                              <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Driver/Authorized Name"
                              value={bulkChallanModal.driverName}
                              onChange={(e) => {
                                const lettersOnly = e.target.value.replace(/[^A-Za-z]/g, '');
                                handleBulkChallanInputChange('driverName', lettersOnly);
                              }}
                              disabled={bulkChallanModal.isCreating}
                              required
                              placeholder="Driver/Authorized Name"
                              error={bulkChallanModal.error.includes('Driver name')}
                            />
                          </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Vehicle Number"
                      value={bulkChallanModal.vehicleNo}
                      onChange={(e) => {
                        const isTruckOrderBy = String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck';
                        handleBulkChallanInputChange(
                          'vehicleNo',
                          isTruckOrderBy ? formatVehicleReferenceNo(e.target.value) : e.target.value
                        );
                      }}
                      disabled={bulkChallanModal.isCreating}
                      required={String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck'}
                      placeholder="Enter vehicle registration number"
                      inputProps={
                        String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck'
                          ? { maxLength: 13 }
                          : undefined
                      }
                      error={
                        (String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck' &&
                          isVehicleReferenceNoInvalid(bulkChallanModal.vehicleNo)) ||
                        bulkChallanModal.error.includes('vehicle number')
                      }
                      helperText={
                        String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck' &&
                        isVehicleReferenceNoInvalid(bulkChallanModal.vehicleNo)
                          ? VEHICLE_REFERENCE_NO_ERROR
                          : ''
                      }
                    />
                  </Grid>
                  
                  {/* Start KM - only show when Order By is "Truck" */}
                  {String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck' && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        // required
                        label="Start KM"
                        type="number"
                        value={bulkChallanModal.start_km || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^[1-9]\d*$/.test(value)) {
                            handleBulkChallanInputChange('start_km', value);
                          }
                        }}
                        onKeyDown={(e) => {
                          // Block unwanted keys
                          if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onWheel={(e) => e.currentTarget.blur()}
                        disabled={bulkChallanModal.isCreating}
                        placeholder="Enter starting kilometer reading"
                        inputProps={{ min: 1, step: 1 }}
                      />
                    </Grid>
                  )}
                  
                  <Grid item xs={12}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={bulkChallanModal.useCustomDispatchPerson}
                            onChange={(e) => handleBulkChallanInputChange('useCustomDispatchPerson', e.target.checked)}
                            disabled={bulkChallanModal.isCreating}
                          />
                        }
                        label="Add custom dispatch person"
                      />
                      {bulkChallanModal.useCustomDispatchPerson ? (
                        <TextField
                          fullWidth
                          label="Custom Dispatch Person Name"
                          value={bulkChallanModal.customDispatchPerson}
                          onChange={(e) => handleBulkChallanInputChange('customDispatchPerson', e.target.value)}
                          disabled={bulkChallanModal.isCreating}
                          required={bulkChallanModal.selectedStatus === 'Dispatch'}
                          placeholder="Enter custom dispatch person name"
                          sx={{ mt: 1 }}
                        />
                      ) : (
                        <FormControl fullWidth required={bulkChallanModal.selectedStatus === 'Dispatch'} sx={{ mt: 1 }}>
                          <InputLabel>Dispatch Person Name</InputLabel>
                          <Select
                            value={bulkChallanModal.dispatchPersonName}
                            label="Dispatch Person Name"
                            onChange={(e) => handleBulkChallanInputChange('dispatchPersonName', e.target.value)}
                            disabled={bulkChallanModal.isCreating}
                          >
                            <MenuItem value="">
                              <em>Select dispatch person</em>
                            </MenuItem>
                            {dispatchPersons.map((person) => (
                              <MenuItem key={person.id} value={person.name}>
                                {person.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Delivery Date"
                      type="date"
                      value={bulkChallanModal.deliveryDate}
                      onChange={(e) => handleBulkChallanInputChange('deliveryDate', e.target.value)}
                      disabled={bulkChallanModal.isCreating}
                      required={bulkChallanModal.selectedStatus === 'Dispatch'}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Email Addresses (Optional)
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          value={bulkChallanModal.emailInput}
                          onChange={(e) => handleBulkChallanInputChange('emailInput', e.target.value)}
                          disabled={bulkChallanModal.isCreating}
                          placeholder="Enter email address"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addEmailToBulkModal();
                            }
                          }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={addEmailToBulkModal}
                          disabled={!bulkChallanModal.emailInput?.trim() || bulkChallanModal.isCreating}
                        >
                          Add
                        </Button>
                      </Box>
                      {bulkChallanModal.emails.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {bulkChallanModal.emails.map((email, index) => (
                            <Chip
                              key={index}
                              label={email}
                              onDelete={() => removeEmailFromBulkModal(email)}
                              size="small"
                              disabled={bulkChallanModal.isCreating}
                            />
                          ))}
                        </Box>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Delivery challans will be sent to these email addresses
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={bulkChallanModal.generateInvoice}
                          onChange={(e) => handleBulkChallanInputChange('generateInvoice', e.target.checked)}
                          disabled={bulkChallanModal.isCreating}
                        />
                      }
                      label="Generate Invoice along with Delivery Challan"
                    />
                  </Grid>
                </>
              )}

              {/* End KM field for Delivered status - only when Truck order has Start KM */}
              {bulkChallanModal.selectedStatus === 'Delivered' && (() => {
                const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id))
                const truckOrderWithStartKm = getTruckOrderWithStartKm(selectedOrders)
                const deliveryChallan = truckOrderWithStartKm?.delivery_challan
                if (!deliveryChallan) return null

                const driverName = bulkChallanModal.driverName || deliveryChallan.driver_name || ''
                const vehicleNo =
                  bulkChallanModal.vehicleNo ||
                  deliveryChallan.vehicle_number ||
                  deliveryChallan.vehicle_no ||
                  deliveryChallan.vehicle_name ||
                  ''
                const startKm =
                  bulkChallanModal.start_km ||
                  (
                    deliveryChallan.start_km !== null && deliveryChallan.start_km !== undefined
                      ? String(deliveryChallan.start_km)
                      : ''
                  )

                return (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Driver Name"
                        value={driverName}
                        disabled
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Vehicle No / Reference No"
                        value={vehicleNo}
                        disabled
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Start KM"
                        value={startKm}
                        disabled
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="End KM"
                        required
                        type="number"
                        value={bulkChallanModal.end_km || ''}
                        onChange={(e) => handleBulkChallanInputChange('end_km', e.target.value)}
                        disabled={bulkChallanModal.isCreating}
                        placeholder="Enter ending kilometer reading"
                        error={(() => {
                          const parsedStartKm = parseFloat(startKm)
                          const endKm = parseFloat(bulkChallanModal.end_km)
                          const hasEndKm = String(bulkChallanModal.end_km || '').trim() !== ''
                          return !hasEndKm || (!isNaN(parsedStartKm) && !isNaN(endKm) && endKm <= parsedStartKm)
                        })()}
                        helperText={(() => {
                          const parsedStartKm = parseFloat(startKm)
                          const endKm = parseFloat(bulkChallanModal.end_km)
                          const hasEndKm = String(bulkChallanModal.end_km || '').trim() !== ''
                          if (!hasEndKm) return 'End KM is required'
                          if (!isNaN(parsedStartKm) && !isNaN(endKm) && endKm <= parsedStartKm) {
                            return 'End KM must be greater than Start KM'
                          }
                          return ''
                        })()}
                      />
                    </Grid>
                  </>
                )
              })()}

              {/* Conditional field for Cancelled status - Cancellation Reason */}
              {bulkChallanModal.selectedStatus === 'Cancelled' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Cancellation Reason"
                    required
                    multiline
                    rows={3}
                    value={bulkChallanModal.cancellationReason || ''}
                    onChange={(e) => handleBulkChallanInputChange('cancellationReason', e.target.value)}
                    disabled={bulkChallanModal.isCreating}
                    placeholder="Enter reason for cancellation"
                  />
                </Grid>
              )}

              {/* Conditional field for Returned status - Return Reason */}
              {bulkChallanModal.selectedStatus === 'Returned' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Return Reason"
                    required
                    multiline
                    rows={3}
                    value={bulkChallanModal.returnReason || ''}
                    onChange={(e) => handleBulkChallanInputChange('returnReason', e.target.value)}
                    disabled={bulkChallanModal.isCreating}
                    placeholder="Enter reason for return"
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button 
            onClick={handleCloseBulkChallanModal}
            disabled={bulkChallanModal.isCreating}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleBulkChallanSubmit}
            variant="contained"
            color="secondary"
            disabled={isBulkSubmitDisabled()}
            sx={{
              '&:hover': {
                backgroundColor: theme.palette.success.main,
              },
              transition: 'all 0.2s ease-in-out'
            }}
            startIcon={
              bulkChallanModal.isCreating ? 
              <CircularProgress size={16} /> : 
              <DeliveryIcon />
            }
          >
            {bulkChallanModal.isCreating ? 'Processing...' : 
              bulkChallanModal.selectedStatus === 'Dispatch' ? 
                `Create ${getEligibleProductsCount()} Challans` : 
                `Update ${getSelectedProductsCount()} Orders`
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Documents via Email Modal */}
      <Dialog
        open={emailDialog.open}
        onClose={handleCloseEmailDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>Send Documents via Email</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Selected orders: {filteredData.filter(p => selectedProducts.includes(p.id)).length}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {emailDialog.error && (
            <Alert severity="error" sx={{ mb: 2 }}>{emailDialog.error}</Alert>
          )}
          {emailDialog.success && (
            <Alert severity="success" sx={{ mb: 2 }}>{emailDialog.success}</Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Emails"
                placeholder="Enter email addresses separated by commas"
                value={emailDialog.emails}
                onChange={(e) => handleEmailDialogChange('emails', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <FormControlLabel
                  control={<Checkbox checked={emailDialog.sendInvoice} onChange={(e) => handleEmailDialogChange('sendInvoice', e.target.checked)} />}
                  label={`Include Invoices (${getAvailableCounts().invoices} available)`}
                />
                <FormControlLabel
                  control={<Checkbox checked={emailDialog.sendDelivery} onChange={(e) => handleEmailDialogChange('sendDelivery', e.target.checked)} />}
                  label={`Include Delivery Challans (${getAvailableCounts().challans} available)`}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailDialog} disabled={emailDialog.isSending}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={emailDialog.isSending ? <CircularProgress size={16} /> : <SendIcon />}
            disabled={emailDialog.isSending}
            onClick={handleEmailSendSubmit}
            sx={{ textTransform: 'none' }}
          >
            {emailDialog.isSending ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Individual Order Email Modal */}
  <Dialog
  open={singleEmailDialog.open}
  onClose={closeSingleOrderEmail}
  maxWidth="sm"
  fullWidth
>
  <DialogTitle>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <EmailIcon color="primary" />
      <Typography variant="h6" fontWeight={600}>Send Documents via Email</Typography>
    </Box>
    {singleEmailDialog.order && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Order ID: {singleEmailDialog.order.order_id} • {singleEmailDialog.order.product_name}
      </Typography>
    )}
  </DialogTitle>

  <DialogContent>
    {singleEmailDialog.error && (
      <Alert severity="error" sx={{ mb: 2 }}>{singleEmailDialog.error}</Alert>
    )}
    {singleEmailDialog.success && (
      <Alert severity="success" sx={{ mb: 2 }}>{singleEmailDialog.success}</Alert>
    )}

    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box display="flex" gap={1}>
          <TextField
            fullWidth
            label="Add Email"
            placeholder="Enter email and click Add"
            value={singleEmailDialog.emailInput}
            onChange={(e) => setSingleEmailDialog(prev => ({ ...prev, emailInput: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmailToSingle(); } }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addEmailToSingle}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Add
          </Button>
        </Box>

        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {singleEmailDialog.emails.map(email => (
            <Chip key={email} label={email} onDelete={() => removeEmailFromSingle(email)} />
          ))}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={singleEmailDialog.sendInvoice}
                onChange={(e) =>
                  setSingleEmailDialog(prev => ({ ...prev, sendInvoice: e.target.checked }))
                }
                disabled={!singleEmailDialog.order?.invoice?.path}
              />
            }
            label={`Include Invoice ${singleEmailDialog.order?.invoice?.path ? '' : '(not available)'}`}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={singleEmailDialog.sendDelivery}
                onChange={(e) =>
                  setSingleEmailDialog(prev => ({ ...prev, sendDelivery: e.target.checked }))
                }
                disabled={!singleEmailDialog.order?.delivery_challan?.path}
              />
            }
            label={`Include Delivery Challan ${singleEmailDialog.order?.delivery_challan?.path ? '' : '(not available)'}`}
          />
        </Box>
      </Grid>
    </Grid>
  </DialogContent>

  <DialogActions>
    <Button onClick={closeSingleOrderEmail} disabled={singleEmailDialog.isSending}>
      Cancel
    </Button>
    <Button
      variant="contained"
      startIcon={
        singleEmailDialog.isSending ? <CircularProgress size={16} /> : <SendIcon />
      }
      disabled={
        singleEmailDialog.isSending ||
        (!singleEmailDialog.order?.invoice?.path && !singleEmailDialog.order?.delivery_challan?.path)
      }
      onClick={submitSingleOrderEmail}
      sx={{ textTransform: 'none' }}
    >
      {singleEmailDialog.isSending ? 'Sending...' : 'Send Email'}
    </Button>
  </DialogActions>
</Dialog>

      {/* Bulk Payment Status Update Modal */}
      <Dialog
        open={bulkPaymentStatusModal.open}
        onClose={handleCloseBulkPaymentStatusModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Update Payment Status
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment()).length} unpaid of{' '}
            {getSelectedProductsCount()} selected order{getSelectedProductsCount() > 1 ? 's' : ''}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {bulkPaymentStatusModal.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {bulkPaymentStatusModal.error}
            </Alert>
          )}

          {bulkPaymentStatusModal.paymentError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {bulkPaymentStatusModal.paymentError}
            </Alert>
          )}

          {bulkPaymentStatusModal.isUpdating && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Updating payment status...
              </Box>
            </Alert>
          )}

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="payment-status-label">Payment Status</InputLabel>
            <Select
              labelId="payment-status-label"
              value={bulkPaymentStatusModal.selectedPaymentStatus || ''}
              label="Payment Status"
              onChange={(e) => {
                const newStatus = e.target.value
                if (newStatus === 'paid') {
                  loadAdvancePaymentMethods()
                }
                setBulkPaymentStatusModal((prev) => ({
                  ...prev,
                  selectedPaymentStatus: newStatus,
                  error: '',
                  paymentError: '',
                  ...(newStatus === 'paid'
                    ? getInlinePaidPaymentStepState(prev.orderPaymentDetails)
                    : newStatus === 'credit'
                      ? getCreditPaymentStepState(prev.orderPaymentDetails)
                      : {
                          step: 'status',
                          paymentOption: '',
                          advanceAmount: '',
                          installmentAmount: '',
                          creditNote: '',
                          paymentMethod: '',
                          paymentMethodConfig: null,
                          paymentFields: {},
                          paymentFiles: {},
                        }),
                }))
              }}
              disabled={
                bulkPaymentStatusModal.isUpdating ||
                loadingPaymentStatusChoices ||
                isBulkPaymentUpdateDisabled()
              }
            >
              {renderPaymentStatusMenuItems({
                hidePaid: ['placed', 'confirmed'].includes(
                  (statusFilter || '').toLowerCase()
                ),
              })}
            </Select>
          </FormControl>

          {(bulkPaymentStatusModal.paymentOption === 'credit' ||
            bulkPaymentStatusModal.paymentOption === 'remaining') && (() => {
            const unpaidCount = getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment()).length
            return (
              <Box sx={{ mt: 2 }}>
                <PaymentDetailsStepContent
                  idPrefix="bulk-payment-inline"
                  order={bulkPaymentStatusModal.summaryOrder}
                  orderPaymentDetails={bulkPaymentStatusModal.orderPaymentDetails}
                  paymentOption={bulkPaymentStatusModal.paymentOption}
                  advanceAmount={bulkPaymentStatusModal.advanceAmount}
                  onAdvanceAmountChange={(e) =>
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      advanceAmount: e.target.value,
                      paymentError: '',
                    }))
                  }
                  installmentAmount={bulkPaymentStatusModal.installmentAmount || ''}
                  onInstallmentAmountChange={(e) => {
                    const value = e.target.value
                    const remaining = Number(
                      bulkPaymentStatusModal.orderPaymentDetails?.remainingAmount ?? 0
                    )
                    if (value !== '' && Number(value) > remaining) {
                      setBulkPaymentStatusModal((prev) => ({
                        ...prev,
                        paymentError: 'Installment amount cannot exceed the remaining amount',
                      }))
                      return
                    }
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      installmentAmount: value,
                      paymentError: '',
                      paymentFields: getCreditChequePaymentFields(
                        prev.paymentFields,
                        value,
                        prev.paymentMethodConfig
                      ),
                    }))
                  }}
                  creditNote={bulkPaymentStatusModal.creditNote || ''}
                  onCreditNoteChange={(e) =>
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      creditNote: e.target.value,
                      paymentError: '',
                    }))
                  }
                  paymentMethod={bulkPaymentStatusModal.paymentMethod}
                  paymentMethodConfig={bulkPaymentStatusModal.paymentMethodConfig}
                  advancePaymentMethods={advancePaymentMethods}
                  loadingAdvancePaymentMethods={loadingAdvancePaymentMethods}
                  onPaymentMethodChange={(e) => {
                    const methodId = e.target.value
                    setBulkPaymentStatusModal((prev) => {
                      const payload = buildPaymentMethodChangePayload(
                        methodId,
                        prev.orderPaymentDetails
                      )
                      if (payload.paymentMethodConfig?.type === 'cheque') {
                        payload.paymentFields = getCreditChequePaymentFields(
                          payload.paymentFields,
                          prev.installmentAmount ?? '',
                          payload.paymentMethodConfig
                        )
                      }
                      return { ...prev, ...payload }
                    })
                  }}
                  paymentFields={bulkPaymentStatusModal.paymentFields}
                  paymentFiles={bulkPaymentStatusModal.paymentFiles}
                  onFieldChange={(key, value) => {
                    if (key === 'cheque_amount' && bulkPaymentStatusModal.orderPaymentDetails) {
                      return
                    }
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      paymentFields: { ...prev.paymentFields, [key]: value },
                      paymentError: '',
                    }))
                  }}
                  onFileChange={(key, file) =>
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      paymentFiles: { ...prev.paymentFiles, [key]: file },
                      paymentError: '',
                    }))
                  }
                  itemsLimit={3}
                  totalLabel="Reference total"
                  summaryBanner={
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Applying {bulkPaymentStatusModal.paymentOption === 'credit' ? 'credit payment' : 'payment'} to{' '}
                      <strong>{unpaidCount}</strong> unpaid order
                      {unpaidCount > 1 ? 's' : ''}.
                    </Typography>
                  }
                />
              </Box>
            )
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseBulkPaymentStatusModal}
            disabled={bulkPaymentStatusModal.isUpdating}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>

          {bulkPaymentStatusModal.paymentOption === 'credit' &&
            shouldShowCreditSubmitButton({
              ...bulkPaymentStatusModal,
              order: bulkPaymentStatusModal.summaryOrder,
            }) && (
            <Button
              variant="contained"
              disabled={
                bulkPaymentStatusModal.isUpdating ||
                !canSubmitCreditPayment({
                  ...bulkPaymentStatusModal,
                  order: bulkPaymentStatusModal.summaryOrder,
                })
              }
              onClick={handleConfirmBulkCreditPaymentDetails}
              startIcon={bulkPaymentStatusModal.isUpdating ? <CircularProgress size={16} /> : <Payment />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {bulkPaymentStatusModal.isUpdating ? 'Updating...' : 'Submit Payment'}
            </Button>
          )}

          {bulkPaymentStatusModal.paymentOption === 'remaining' && (
            <Button
              variant="contained"
              disabled={
                bulkPaymentStatusModal.isUpdating ||
                !canSubmitPaidPayment(bulkPaymentStatusModal)
              }
              onClick={handleConfirmBulkPaidPaymentDetails}
              startIcon={bulkPaymentStatusModal.isUpdating ? <CircularProgress size={16} /> : <Payment />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {bulkPaymentStatusModal.isUpdating ? 'Updating...' : 'Submit Payment'}
            </Button>
          )}

          {bulkPaymentStatusModal.selectedPaymentStatus !== 'paid' &&
            bulkPaymentStatusModal.selectedPaymentStatus !== 'credit' && (
            <Button
              onClick={handleBulkPaymentStatusUpdateFromModal}
              variant="contained"
              color="primary"
              disabled={
                !bulkPaymentStatusModal.selectedPaymentStatus ||
                bulkPaymentStatusModal.isUpdating ||
                isBulkPaymentUpdateDisabled()
              }
              startIcon={bulkPaymentStatusModal.isUpdating ? <CircularProgress size={16} /> : <Payment />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {bulkPaymentStatusModal.isUpdating ? 'Updating...' : 'Update Payment'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

    </Box>
  )
}

export default BulkProductList  